// ── Interface ──────────────────────────────────────────────────────────────────

export type PestType =
  | 'insect'
  | 'disease'
  | 'fungal'
  | 'animal'
  | 'deficiency'
  | 'physiological'
  | 'other';

export interface PestSolution {
  name: string;
  description: string;
}

export interface PestLookalike {
  name: string;
  distinction: string;
}

export interface PestSource {
  kind: 'curated' | 'community' | 'generated';
  trust: 'curated' | 'community' | 'generated';
  reviewed?: boolean;
  generatedAt?: string;
  promotedAt?: string;
  promotedBy?: string | null;
  promotedToBuiltin?: boolean;
  builtinId?: string;
  builtinPromotedAt?: string;
  builtinPromotedBy?: string | null;
}

export interface Pest {
  id: string;
  name: string;
  scientificName?: string | null;
  status?: 'draft' | 'approved';
  type: PestType;
  /** Symptom tags used for quick-filter: holes | yellowing | powder | wilt | sticky | chewing | spots | stunted */
  tags: string[];
  /**
   * Environmental conditions that favour this pest/disease.
   * Canonical values: 'rain' | 'humid' | 'cool' | 'warm' | 'hot' | 'dry' | 'wet-soil'
   * Used by pestMatcher to surface relevant risks when weather conditions match.
   * Future pests with these triggers are automatically picked up — no code changes required.
   */
  weatherTriggers?: string[];
  description: string;
  symptoms: string[];
  affectedPlants: string[];
  causes?: string[];
  lifecycle?: string;
  spread?: string;
  organicSolutions: PestSolution[];
  preventionTips: string[];
  lookalikes?: PestLookalike[];
  image?: string | null;
  source?: PestSource;
}

// ── Data ──────────────────────────────────────────────────────────────────────

export const PESTS: Pest[] = [

  // ── INSECTS ────────────────────────────────────────────────────────────────

  {
    id: 'aphids',
    image: '/images/pests/aphids.png',
    name: 'Aphids',
    type: 'insect',
    tags: ['sticky', 'yellowing', 'stunted'],
    description: 'Soft-bodied sap-sucking insects 1 - 3 mm long that cluster on new growth and leaf undersides in large colonies. They can be green, black, white, or grey depending on species. In warm weather aphid populations double every few days, making early action essential.',
    symptoms: [
      'Curled, distorted, or puckered new growth at shoot tips',
      'Sticky, shiny honeydew coating on leaves and stems below colonies',
      'Black sooty mould growing on honeydew deposits',
      'Yellowing or stunted leaves, particularly on young growth',
      'Ants running up and down stems, "farming" aphid colonies for their honeydew',
    ],
    affectedPlants: ['Tomato', 'Bell Pepper', 'Kale', 'Broccoli', 'Cabbage', 'Lettuce', 'Spinach', 'Brussels Sprouts', 'Cauliflower', 'Nasturtium', 'Calendula', 'Sunflower', 'Borage', 'Peas', 'Fennel'],
    organicSolutions: [
      { name: 'Strong Water Blast', description: 'Direct a firm jet of water from a hose at affected growth, particularly leaf undersides. This physically dislodges the colony. Aphids that fall to the soil rarely make it back to the plant. Repeat every 2 - 3 days.' },
      { name: 'Insecticidal Soap Spray', description: 'A 1 - 2% solution of pure castile soap in water kills aphids on contact by disrupting their cell membranes. Spray directly onto colonies, covering undersides of leaves. Safe for most beneficial insects once the spray dries.' },
      { name: 'Neem Oil Spray', description: 'Mix 2 tablespoons of neem oil with 1 teaspoon of castile soap per litre of warm water. Spray all surfaces, especially leaf undersides. Apply at dusk to avoid harming pollinators and beneficial insects. Neem disrupts aphid feeding and reproduction; repeat every 5–7 days for three weeks.' },
      { name: 'Kaolin Clay Spray', description: 'Mix wettable kaolin clay per package directions and spray onto all plant surfaces. The fine particle film confuses and deters aphids without harming soil biology or beneficial insects. Reapply after rain. Completely safe and leaves no toxic residue.' },
      { name: 'Encourage Ladybirds and Lacewings', description: 'A single ladybird can consume 50 aphids per day; a lacewing larva can eat 200 per week. Plant dill, yarrow, fennel, and calendula nearby to attract and sustain these predators naturally.' },
    ],
    preventionTips: [
      'Plant nasturtiums as sacrificial trap crops - aphids strongly prefer them and can be removed en masse from a single plant',
      'Avoid over-fertilising with nitrogen, which produces the soft, sappy growth that aphids prefer over tougher plant tissue',
      'Maintain a diverse garden with plenty of flowering plants to sustain permanent populations of aphid predators — ladybirds, lacewings, hoverflies, and parasitic wasps are your best long-term aphid control',
      'Boost plant immunity with regular foliar applications of aerated compost tea — plants with strong biological soil support are measurably more resistant to aphid colonisation',
    ],
  },

  {
    id: 'tomato-hornworm',
    image: '/images/pests/tomato-hornworm.png',
    name: 'Tomato Hornworm',
    type: 'insect',
    tags: ['holes', 'chewing'],
    description: 'The larval stage of the Five-Spotted Hawk Moth, this caterpillar grows up to 10 cm long and is bright green with white diagonal stripes and a distinctive rear horn. Its camouflage is excellent and it can strip a tomato plant rapidly before being noticed.',
    symptoms: [
      'Large sections of leaves completely consumed, often leaving only the main stem',
      'Dark green barrel-shaped droppings on leaves and soil directly below the plant',
      'Large, ragged bite marks on ripening fruit',
      'Damage that appears suddenly and progresses very quickly',
      'Dark green caterpillar blending into stems, usually found on the upper third of the plant',
    ],
    affectedPlants: ['Tomato', 'Eggplant', 'Bell Pepper', 'Jalapeño Pepper'],
    organicSolutions: [
      { name: 'Hand-Picking', description: 'Inspect plants at dusk and dawn when hornworms are most visible. Wear gloves and drop them into a bucket of soapy water. Use a UV torch at night - hornworms fluoresce bright green under blacklight, making them easy to spot.' },
      { name: 'Bacillus thuringiensis (Bt)', description: 'Bt kurstaki is a naturally occurring soil bacteria that, when ingested by caterpillars, produces proteins toxic to their gut. Spray foliage thoroughly, focusing on areas where feeding damage is visible. Effective only on actively feeding larvae; breaks down in UV light within a few days.' },
      { name: 'Kaolin Clay', description: 'Apply kaolin clay spray to foliage as a preventive barrier. The particle film deters adult moths from laying eggs on coated leaves. Reapply after rain or every 7–10 days through peak season. Harmless to beneficial insects and soil.' },
      { name: 'Encourage Parasitic Wasps', description: 'Braconid wasps (Cotesia congregatus) lay eggs inside hornworms. If you find a hornworm covered in small white cocoons like grains of rice, leave it - the emerging wasps will parasitise future generations. Plant dill, parsley, and fennel to attract and sustain them.' },
    ],
    preventionTips: [
      'Lightly cultivate the soil around plants in autumn to expose pupae to birds and cold temperatures',
      'Plant dill, parsley, and fennel from the beginning of the season to establish populations of braconid wasps and other beneficial insects before hornworm season',
      'Inspect plants weekly from midsummer - a single hornworm found at 2 cm can be removed before any visible damage occurs',
      'Build a diverse beneficial insect habitat throughout the garden — parasitic wasps, ground beetles, and predatory bugs naturally keep hornworm populations in check in a biologically active garden',
    ],
  },

  {
    id: 'cabbage-worm',
    image: '/images/pests/cabbage-worm.png',
    name: 'Cabbage Worm',
    type: 'insect',
    tags: ['holes', 'chewing'],
    description: 'The pale green caterpillar of the Small White butterfly (Pieris rapae), typically 2 - 3 cm long and covered in fine hairs. Adults are the familiar white butterflies seen fluttering around brassicas in summer. Both larvae and eggs are superbly camouflaged against the plants they infest.',
    symptoms: [
      'Ragged, irregular holes chewed through leaves - midribs and larger veins often left intact',
      'Small round green droppings scattered across leaves and tucked into leaf folds',
      'Small white butterflies hovering and landing repeatedly on brassica leaves',
      'Pale green larvae found on the underside of leaves, often near the feeding damage',
      'Head of broccoli or cauliflower tunnelled into and soiled with droppings',
    ],
    affectedPlants: ['Broccoli', 'Cabbage', 'Cauliflower', 'Brussels Sprouts', 'Kale', 'Arugula', 'Turnip', 'Radish'],
    organicSolutions: [
      { name: 'Fine Mesh Row Covers', description: 'Insect netting or horticultural fleece placed over brassicas immediately after transplanting prevents adult butterflies from reaching leaves to lay eggs. This is the single most effective method - physical exclusion is close to 100% successful when applied early.' },
      { name: 'Bacillus thuringiensis (Bt)', description: 'Spray Bt kurstaki on all leaf surfaces as soon as larvae are detected. The bacteria must be ingested, so thorough coverage of leaf undersides where small larvae feed is essential. Reapply after rain or every 5 - 7 days.' },
      { name: 'Hand-Picking Eggs and Larvae', description: 'Inspect leaf undersides for pale yellow oval eggs laid singly and flatten them. Larger larvae can be hand-picked into soapy water. Most effective for small gardens or when checking becomes a brief daily routine.' },
      { name: 'Kaolin Clay', description: 'Spray kaolin clay onto brassica leaves as a barrier that confuses adult butterflies seeking plants to lay eggs on. Most effective on young plants before heavy pressure establishes. Reapply after rain.' },
      { name: 'Aromatic Herb Companions', description: 'Interplanting brassicas with thyme, sage, dill, and rosemary is thought to confuse egg-laying females by masking the crucifer host-plant scent. Not fully reliable alone but reduces pressure when combined with other measures.' },
    ],
    preventionTips: [
      'Install insect mesh from the day of planting - once butterflies find the plants, control becomes reactive',
      'Rotate brassicas to a new bed each season to prevent caterpillar populations building in the same soil',
      'Avoid leaving brassica stubble in the ground after harvest - remove or dig in promptly',
      'Attract ground beetles, parasitic wasps, and predatory bugs by planting dill, fennel, yarrow, and marigolds in and around the brassica bed — a diverse beneficial insect population is your most durable long-term control',
    ],
  },

  {
    id: 'japanese-beetle',
    image: '/images/pests/japanese-beetle.png',
    name: 'Japanese Beetle',
    type: 'insect',
    tags: ['holes', 'chewing'],
    description: 'A metallic blue-green beetle with copper wing covers, about 12 mm long. Adults skeletonise leaves and damage flowers and fruit in midsummer, while their c-shaped white grubs live underground and feed on roots. They aggregate on favoured plants, and a single plant can attract dozens.',
    symptoms: [
      'Leaves consumed between veins, leaving a brown, papery, lace-like skeleton',
      'Clusters of beetles actively feeding together on the same plant or area of the plant',
      'Damage concentrated in June through August during adult emergence',
      'Damaged fruit with surface feeding scars',
      'Dead patches in nearby lawns from grub root damage',
    ],
    affectedPlants: ['Grape', 'Apple', 'Peach', 'Cherry', 'Pear', 'Raspberry', 'Blackberry', 'Corn', 'Green Beans', 'Asparagus', 'Echinacea', 'Black-Eyed Susan', 'Zinnia', 'Dahlia'],
    organicSolutions: [
      { name: 'Hand-Picking in Early Morning', description: 'Beetles are sluggish when cool. In the morning, hold a bucket of soapy water beneath the plant and shake the branch - beetles fall directly into it. This is most effective in early July at peak adult emergence and removes beetles before they release aggregation pheromones.' },
      { name: 'Milky Spore', description: 'Milky spore (Paenibacillus popilliae) is a naturally occurring soil bacteria specific to Japanese beetle grubs. Apply as a powder to lawn and garden soil. It takes 1 - 3 seasons to establish but then provides control for 10 - 20 years by building in the grub population.' },
      { name: 'Neem Oil', description: 'Neem oil sprayed on foliage deters adult feeding and acts as an insect growth regulator disrupting larval development when grubs ingest treated plant material. Apply at dusk to avoid harming pollinators and beneficial insects on open flowers.' },
      { name: 'Beneficial Nematodes', description: 'Apply Heterorhabditis bacteriophora nematodes as a soil drench in late summer when grubs are small and vulnerable. Water the soil before and after application. Nematodes are completely harmless to plants, earthworms, and humans and provide lasting grub control.' },
      { name: 'Row Covers', description: 'Drape fine mesh netting over high-value plants such as grape vines and raspberry canes during the 6–8 week adult emergence window in midsummer to provide complete protection.' },
    ],
    preventionTips: [
      'Apply milky spore to lawn areas in spring and autumn over several seasons to steadily reduce the soil grub population',
      'Encourage ground-foraging birds — starlings, robins, and blackbirds feed heavily on grubs when turning soil and foraging in lawn areas',
      'Plant yarrow, fennel, and native flowering plants to attract parasitic flies and wasps that prey on Japanese beetle larvae and adults',
      'Avoid planting beetles\' most-favoured plants (grape, raspberry, rose) in groups that create a concentration attracting aggregating beetles',
    ],
  },

  {
    id: 'squash-bug',
    image: '/images/pests/squash-bug.png',
    name: 'Squash Bug',
    type: 'insect',
    tags: ['wilt', 'yellowing'],
    description: 'A flat, grey-brown bug about 15 mm long that overwinters as an adult in garden debris and emerges in late spring to attack cucurbit crops. By piercing plant tissue and injecting toxic saliva, it can cause rapid vine death - a problem compounded by its ability to carry cucurbit yellow vine disease.',
    symptoms: [
      'Vines wilting rapidly and failing to recover even when watered',
      'Yellow spots on leaves that enlarge, turn brown, and become dry and papery',
      'Bronze or yellow discolouration spreading across vine sections',
      'Clusters of shiny copper-coloured eggs in neat diamond patterns on leaf undersides',
      'Adults and nymphs congregating on stems, particularly near the crown',
    ],
    affectedPlants: ['Zucchini', 'Butternut Squash', 'Cucumber', 'Cantaloupe', 'Watermelon'],
    organicSolutions: [
      { name: 'Remove Egg Clusters', description: 'Inspect leaf undersides weekly from late May. Squash bug eggs are copper-coloured ovals laid in precise clusters - scrape them off with an old credit card, tape, or sticky roller and destroy them. Removing eggs before they hatch is by far the most impactful control measure.' },
      { name: 'Trap Boards', description: 'Lay pieces of cardboard or wooden boards near squash plants in the evening. Adults shelter under them at dusk. Collect and dump them into soapy water each morning before temperatures rise and beetles become active.' },
      { name: 'Diatomaceous Earth', description: 'Apply food-grade diatomaceous earth as a targeted barrier around the base of plants and near the crown where bugs congregate. Apply as a targeted barrier only — it harms all insects including beneficial ones, so avoid broadcasting it widely. Reapply after rain or irrigation.' },
      { name: 'Kaolin Clay', description: 'Spray kaolin clay onto all plant surfaces — the particle coating makes the plant surface inhospitable to squash bugs without harming soil life or beneficial insects. Most effective applied early and reapplied regularly through the season.' },
      { name: 'Insecticidal Soap', description: 'Insecticidal soap is effective against young nymphs. Spray directly on small nymphs shortly after egg hatch, covering the bodies entirely. Adults have a thicker cuticle that makes soap spray less effective.' },
    ],
    preventionTips: [
      'Clear all plant debris thoroughly at the end of the season - adult squash bugs overwinter in crop residues, wood piles, and debris near the garden',
      'Start cucurbits from transplants to give plants a 4–5 week head start before the main pest emergence',
      'Use row covers from planting until flowering begins; the protected early period is when populations establish',
      'Plant dill, nasturtiums, and marigolds near cucurbits to attract parasitic wasps and other beneficial insects that prey on squash bug eggs and nymphs',
    ],
  },

  {
    id: 'cucumber-beetle',
    image: '/images/pests/cucumber-beetle.png',
    name: 'Cucumber Beetle',
    type: 'insect',
    tags: ['holes', 'wilt'],
    description: 'Small yellow-green beetles (6 mm) with either black spots or black stripes. Both species cause feeding damage but are most damaging as vectors of bacterial wilt and cucumber mosaic virus, which they transmit when feeding. A plant can look healthy one week and be wilting irreversibly the next after beetle feeding.',
    symptoms: [
      'Round holes eaten in leaves and flowers, often while the plant still appears otherwise healthy',
      'Sudden wilting that spreads rapidly following heavy beetle feeding - a sign of bacterial wilt transmission',
      'Stunted, yellowing seedlings in early season when beetle pressure is highest',
      'Adults visible on flowers and the undersides of leaves',
      'Scarring and scalloping on leaf margins and stem tissue',
    ],
    affectedPlants: ['Cucumber', 'Zucchini', 'Butternut Squash', 'Watermelon', 'Cantaloupe', 'Corn', 'Green Beans'],
    organicSolutions: [
      { name: 'Row Covers on Seedlings', description: 'Cover all cucurbit transplants with fine mesh netting immediately after planting. Remove covers when flowers appear for pollinator access. Protecting plants during their first 3 - 4 weeks prevents the most damaging early-season feeding.' },
      { name: 'Kaolin Clay', description: 'A spray-on coating of kaolin clay (sold as a wettable powder) creates a physical barrier on plant surfaces that irritates and deters feeding beetles. Apply to seedlings and young transplants every 7 - 10 days and after rain.' },
      { name: 'Yellow Sticky Traps', description: 'Cucumber beetles are attracted to yellow. Place sticky yellow cards at plant height near cucurbit beds to monitor population levels and reduce adult numbers during peak emergence.' },
      { name: 'Companion Planting with Nasturtiums and Radishes', description: 'Nasturtiums planted as a border are reported to deter cucumber beetles. Radishes intercropped with cucumbers attract beetle attention to the radishes, protecting the more valuable crop.' },
    ],
    preventionTips: [
      'Delay planting cucurbits by 2–3 weeks where possible to miss the peak emergence of overwintered adults in late spring',
      'Rotate cucurbit crops on a 3-year cycle - beetles overwinter in the soil and in crop debris and return to the same areas',
      'Choose wilt-tolerant cucumber varieties such as \'County Fair 83\' or \'Marketmore 76\' in areas with known bacterial wilt pressure',
      'Plant dill, fennel, and yarrow in or near cucurbit beds to attract beneficial insects — tachinid flies and ground beetles are natural predators of cucumber beetle larvae',
    ],
  },

  {
    id: 'flea-beetle',
    image: '/images/pests/flea-beetle.png',
    name: 'Flea Beetle',
    type: 'insect',
    tags: ['holes'],
    description: 'Tiny jumping beetles (1 - 3 mm) that create characteristic shotgun-blast patterns of small holes in leaves. Multiple species attack different crops; the crucifer flea beetle is most common in vegetable gardens. Seedlings are most vulnerable - adult beetles overwinter in leaf litter and emerge hungry in early spring.',
    symptoms: [
      'Numerous small, round holes peppered uniformly across leaf surfaces - the "shothole" pattern',
      'Seedlings and recent transplants most severely affected; leaves may be almost entirely consumed',
      'Tiny dark beetles that jump explosively when the plant is disturbed',
      'Severe damage slowing or killing seedlings; established plants usually grow through moderate damage',
      'Pitting or scarring on the surface of root crops in species that feed below ground',
    ],
    affectedPlants: ['Arugula', 'Radish', 'Kale', 'Broccoli', 'Cabbage', 'Cauliflower', 'Turnip', 'Lettuce', 'Swiss Chard', 'Eggplant', 'Tomato', 'Spinach'],
    organicSolutions: [
      { name: 'Row Covers', description: 'Lightweight insect mesh placed over transplants or seeds at the time of planting prevents adult beetles from reaching plants. Most effective in spring and early autumn when flea beetle pressure peaks. Remove covers as plants mature and become tolerant.' },
      { name: 'Diatomaceous Earth', description: 'Dust food-grade diatomaceous earth over the soil surface around vulnerable seedlings as a targeted barrier. Apply as a targeted barrier only — it harms all insects including beneficial ones, so keep applications narrow and focused. Reapply after rain or heavy dew.' },
      { name: 'Sticky Yellow Traps', description: 'Flea beetles are attracted to yellow. Yellow sticky cards placed among young transplants at canopy height reduce adults during peak damage periods and provide population monitoring.' },
      { name: 'Kaolin Clay Spray', description: 'Regular kaolin clay application creates a fine white coating on plant surfaces that physically deters flea beetles from landing and feeding. Apply every 7 days to seedlings and young transplants. One of the most effective and completely non-toxic options available.' },
    ],
    preventionTips: [
      'Delay transplanting brassica seedlings by 2–3 weeks after the spring flush to miss the peak population of hungry overwintered adults',
      'Remove brassica crop residues promptly after harvest - adults overwinter in old stems and dead leaf litter',
      'Plant aromatic companions such as basil, mint, and catnip near susceptible seedlings to mask host-plant volatiles',
      'Create habitat for ground beetles — they are voracious flea beetle predators. A mulched perennial border or log pile near the vegetable garden supports permanent ground beetle populations',
    ],
  },

  {
    id: 'whitefly',
    image: '/images/pests/whitefly.png',
    name: 'Whitefly',
    type: 'insect',
    tags: ['sticky', 'yellowing'],
    description: 'Tiny (1 - 2 mm) white-winged sap-sucking insects that cluster in large numbers on leaf undersides. They excrete sticky honeydew, promote sooty mould, and can spread plant viruses. Populations build extremely rapidly in warm, sheltered conditions and are a particular challenge in greenhouses and polytunnels.',
    symptoms: [
      'Clouds of tiny white insects rise up when a plant is disturbed',
      'Sticky, shiny honeydew coating on upper leaf surfaces and everything beneath the plant',
      'Black sooty mould growing over honeydew deposits',
      'Mottled, pale, or yellowing leaves, especially on lower and older growth',
      'White scale-like pupae stuck to leaf undersides; fine pale stippling from feeding damage',
    ],
    affectedPlants: ['Tomato', 'Bell Pepper', 'Eggplant', 'Kale', 'Cabbage', 'Broccoli', 'Sweet Potato', 'Basil', 'Cucumber', 'Jalapeño Pepper'],
    organicSolutions: [
      { name: 'Yellow Sticky Traps', description: 'Whiteflies are strongly attracted to bright yellow. Hang sticky cards just above the canopy - essential for monitoring and can significantly reduce adult populations. Replace when covered. Most effective when deployed early before populations establish.' },
      { name: 'Neem Oil Spray', description: 'Diluted neem oil (2 tablespoons per litre) applied to leaf undersides smothers eggs and nymphs and deters adults from settling. Apply at dusk to avoid harming pollinators and beneficial insects. Repeat every 5–7 days for three consecutive weeks.' },
      { name: 'Kaolin Clay', description: 'Spray kaolin clay onto all plant surfaces. The fine particle film disorients whiteflies and reduces their ability to identify and settle on host plants. Completely harmless to soil and beneficial insects; reapply after rain.' },
      { name: 'Introduce Encarsia formosa', description: 'This tiny parasitic wasp lays its eggs inside whitefly pupae, killing them. Available from biological supply companies, it is highly effective in enclosed spaces such as polytunnels and greenhouses. Release at the first sign of infestation.' },
      { name: 'Reflective Mulch', description: 'Silver or reflective mulch disorients whiteflies and other flying insects by reflecting UV light upward, making it harder for them to locate host plants. Particularly useful for peppers and tomatoes in warm, sunny gardens.' },
    ],
    preventionTips: [
      'Inspect purchased transplants thoroughly before introducing them - whiteflies are frequently introduced on greenhouse-raised plants',
      'Encourage predatory insects (ladybirds, lacewings, hoverflies) through diverse companion plantings throughout the garden — these insects are your permanent whitefly control',
      'Avoid high-nitrogen fertilising in midsummer, which produces lush tender growth that whiteflies prefer',
      'Apply compost tea as a foliar spray every two weeks — the beneficial microorganisms colonise leaf surfaces and compete with pest establishment',
    ],
  },

  {
    id: 'spider-mites',
    image: '/images/pests/spider-mites.png',
    name: 'Spider Mites',
    type: 'insect',
    tags: ['yellowing', 'spots'],
    description: 'Microscopic eight-legged arachnids (not technically insects) that pierce plant cells to feed on sap. Nearly invisible to the naked eye, they are most problematic in hot, dry weather and can build to devastating population densities within days. The fine webbing they produce is often the first sign of a significant infestation.',
    symptoms: [
      'Fine silk webbing on leaf undersides and between stems',
      'Stippled, bronzed, or silvery discolouration on leaf surfaces - each spot is a dead feeding site',
      'Leaves becoming dry, papery, and dropping prematurely',
      'Dust-like movement visible when inspecting the underside of leaves with a hand lens',
      'Yellow or reddish mottling spreading uniformly across the entire leaf surface',
    ],
    affectedPlants: ['Tomato', 'Cucumber', 'Zucchini', 'Butternut Squash', 'Watermelon', 'Cantaloupe', 'Corn', 'Strawberry', 'Raspberry', 'Eggplant', 'Bell Pepper'],
    organicSolutions: [
      { name: 'Forceful Water Spray', description: 'A strong jet of water directed at leaf undersides physically removes mites and disrupts colonies. Raising humidity also suppresses reproduction since mites thrive in hot, dry conditions. Repeat every 2 - 3 days for two weeks.' },
      { name: 'Neem Oil', description: 'Neem oil functions as both an insecticide and an acaricide. Apply a thorough spray to all leaf surfaces, especially undersides, every 7 days during infestation. Apply at dusk to avoid harming pollinators and beneficial insects. The azadirachtin in neem disrupts mite feeding and egg hatching.' },
      { name: 'Predatory Mites', description: 'Phytoseiulus persimilis and Neoseiulus californicus are commercially available predatory mites that are voracious spider mite predators. Release them when the first signs of infestation appear. They will establish and self-regulate as long as prey is present.' },
      { name: 'Castile Soap Spray', description: 'A 2% solution of pure castile soap kills spider mites on contact by disrupting their outer membrane. Spray all surfaces thoroughly, focusing on leaf undersides, every 3–4 days during active infestations. Safe for beneficial insects once the spray has dried.' },
    ],
    preventionTips: [
      'Keep plants adequately watered — drought-stressed plants are far more susceptible to spider mite outbreaks. Mulching around the base helps maintain soil moisture and reduces plant stress',
      'Avoid dusty conditions around plants, particularly on dry roads or paths; dust particles on leaves favour mite colonisation',
      'Build populations of natural predators — predatory mites, ladybirds, and lacewings maintain spider mite populations at low levels when present. Plant dill, yarrow, fennel, and other umbrella-flowered plants to support these beneficial insects year-round',
      'Apply compost tea as a foliar spray during warm, dry weather — the beneficial organisms help suppress the leaf surface conditions that spider mites prefer',
    ],
  },

  {
    id: 'slugs-and-snails',
    image: '/images/pests/slugs-and-snails.png',
    name: 'Slugs and Snails',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['rain', 'humid', 'cool'],
    description: 'Soft-bodied molluscs that rasp holes in plant tissue using a toothed radula tongue. They are nocturnal and most active in wet weather and mild temperatures. Seedlings and low-growing soft-leaved crops are most vulnerable, and a single night\'s activity can destroy an entire bed of newly transplanted lettuces.',
    symptoms: [
      'Irregular ragged holes in leaves, with no insect visible during daytime inspections',
      'Silvery slime trails on leaves, on soil, and on nearby hard surfaces in the morning',
      'Entire seedlings severed at soil level or completely consumed overnight',
      'Hollow channels bored through soft fruits and root crops',
      'Damage worst during and immediately after periods of rain or irrigation',
    ],
    affectedPlants: ['Lettuce', 'Spinach', 'Kale', 'Arugula', 'Strawberry', 'Basil', 'Swiss Chard', 'Beet', 'Cabbage', 'Broccoli', 'Zucchini', 'Sweet Potato', 'Corn'],
    organicSolutions: [
      { name: 'Iron Phosphate Pellets', description: 'Ferric phosphate pellets (sold as Sluggo or similar) are fully approved for organic use and are safe for wildlife, pets, and children. Slugs and snails ingest the pellets and stop feeding immediately, dying within a few days. Scatter lightly around vulnerable plants.' },
      { name: 'Beer Traps', description: 'Bury a container (yoghurt pot, small jar) flush with the soil surface and fill with cheap beer or a sugar-yeast-water solution. Slugs are attracted to the fermentation smell, fall in, and drown. Empty and refill every 2 - 3 days.' },
      { name: 'Copper Tape Barriers', description: 'Copper tape attached to pot rims or raised bed edging gives slugs and snails a mild electrical reaction when they attempt to cross it. Keep the tape clean of soil and debris to remain effective.' },
      { name: 'Encourage Natural Predators', description: 'Ground beetles, hedgehogs, frogs, toads, thrushes, and slow-worms all eat slugs voraciously. Create habitat: log piles, pond margins, and mulched border areas support populations that naturally regulate slugs.' },
    ],
    preventionTips: [
      'Water in the early morning rather than evening so the soil surface dries before peak slug activity at night',
      'Surround seedlings with a gritty top-dressing of sharp grit, crushed eggshell, or coarse sand - slugs dislike crossing rough textures',
      'A garden rich in wildlife, including toads, hedgehogs, and ground beetles, rarely has serious slug problems - biodiversity is the best long-term solution',
    ],
  },

  {
    id: 'colorado-potato-beetle',
    image: '/images/pests/colorado-potato-beetle.png',
    name: 'Colorado Potato Beetle',
    type: 'insect',
    tags: ['holes', 'chewing'],
    description: 'A distinctive beetle with cream wings boldly striped in black, about 10 mm long. Both adults and the bright orange-red larvae cause severe defoliation of nightshade family plants. It can develop resistance to insecticides rapidly, making organic management approaches more durable than chemical options.',
    symptoms: [
      'Leaves eaten from the margins inward, leaving only the main veins in severe cases',
      'Bright orange-red larvae in clusters on the undersides of leaves',
      'Rows of bright yellow-orange eggs laid in neat clusters on leaf undersides',
      'Rapid defoliation advancing up the plant and spreading between plants',
      'Striped adults visible on foliage from late spring through summer',
    ],
    affectedPlants: ['Tomato', 'Eggplant', 'Bell Pepper', 'Jalapeño Pepper'],
    organicSolutions: [
      { name: 'Hand-Picking', description: 'Check leaf undersides daily for yellow-orange egg clusters and remove them. Larvae and adults can be shaken or brushed into a container of soapy water. In a small garden, this daily routine prevents populations establishing.' },
      { name: 'Bacillus thuringiensis var. tenebrionis', description: 'The tenebrionis strain of Bt specifically targets Colorado potato beetle larvae. Apply as a spray when small larvae first hatch. Coverage of leaf undersides is essential. More effective on young larvae than on adults.' },
      { name: 'Deep Mulch', description: 'A deep straw or wood chip mulch (15+ cm) makes it significantly harder for adults emerging from the soil in spring to reach plants. Combined with row covers, this physical strategy prevents the first wave of spring pressure.' },
      { name: 'Kaolin Clay', description: 'Spray kaolin clay onto all plant surfaces starting at transplant time. The particle film deters adults from feeding and interferes with egg-laying. Reapply after rain or every 7–10 days.' },
      { name: 'Row Covers', description: 'Cover transplants with fine mesh from the day of planting until the first emergence wave passes — typically two to three weeks. Ensure covers are sealed at edges so adults cannot crawl under.' },
    ],
    preventionTips: [
      'Rotate nightshade family crops on a 3–4 year cycle - adults overwinter in the top 5 cm of soil near where crops grew the previous season',
      'Plant nasturtiums, marigolds, and dill near nightshade crops to attract parasitic wasps and predatory insects that naturally regulate Colorado potato beetle populations',
      'Time transplanting so plants are large and resilient before the main adult emergence in late spring',
      'Create beneficial insect habitat — parasitic wasps, lacewings, and predatory stinkbugs are major natural enemies of Colorado potato beetle. A diverse, flowering garden perimeter is your most durable defence',
    ],
  },

  {
    id: 'earwigs',
    image: '/images/pests/earwigs.png',
    name: 'Earwigs',
    type: 'insect',
    tags: ['holes', 'chewing'],
    description: 'Brown nocturnal insects (12 - 15 mm) with characteristic rear pincers. They are omnivores - they eat both plant material and pest insects including aphids, mites, and small caterpillars. In most gardens they are more beneficial than harmful; significant damage occurs only when populations are very high or plants are already stressed.',
    symptoms: [
      'Ragged, irregular holes in petals and soft flower tissue, particularly in dahlias, calendula, and zinnias',
      'Seedlings and young transplants with notched or eaten leaves discovered in the morning',
      'Stippled or pitted leaf surfaces on soft-leaved plants',
      'Visible insects found in curled leaves, inside flowers, under mulch, or in any crevice at night',
    ],
    affectedPlants: ['Lettuce', 'Strawberry', 'Dahlia', 'Calendula', 'Corn', 'Zinnia', 'Sweet Alyssum'],
    organicSolutions: [
      { name: 'Trap Tubes', description: 'Roll damp newspaper into a loose cylinder or use short lengths of garden hose placed near affected plants. Earwigs shelter in them overnight. Collect traps each morning and relocate the earwigs well away from the garden, or dunk in soapy water.' },
      { name: 'Diatomaceous Earth', description: 'Apply a light dusting of food-grade diatomaceous earth around the base of vulnerable plants as a targeted barrier. Apply as a targeted barrier only — it harms all insects including beneficial ones — so keep it narrow and focused on paths of travel, not broadcast across the bed.' },
      { name: 'Sticky Barriers', description: 'Apply a band of tanglefoot or horticultural grease around pot rims and stakes to prevent earwigs climbing up to reach plants from the ground.' },
    ],
    preventionTips: [
      'Earwigs are largely beneficial - they consume aphids, mites, and other pests. Control only when damage is clearly significant and not tolerable',
      'Reduce refuges near vulnerable plants: lift pots, stones, and debris during the day that provide earwig shelter',
      'Earwig populations self-regulate when ground beetles, birds, and other predators are present in a healthy garden ecosystem',
    ],
  },

  {
    id: 'codling-moth',
    image: '/images/pests/codling-moth.png',
    name: 'Codling Moth',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm', 'hot'],
    description: 'The larva of a small grey-brown moth (Cydia pomonella), 12 - 20 mm long and cream-coloured with a brown head. Adults emerge in late spring and lay eggs on developing fruit or leaves. Larvae tunnel directly to the core to feed on seeds, leaving a telltale entry hole plugged with brown frass. It is the most economically significant apple pest worldwide.',
    symptoms: [
      'Small entry holes on the fruit surface, surrounded by a small pile of brown-red frass',
      'Internal tunnels running directly to the core, packed with brown granular frass',
      'Fruit dropping prematurely in early - midsummer before it is ripe',
      'A small circular exit hole left in the skin when the mature larva exits to pupate',
      'Pink-cream caterpillar found inside damaged fruit near the core',
    ],
    affectedPlants: ['Apple', 'Pear', 'Quince', 'Walnut'],
    organicSolutions: [
      { name: 'Kaolin Clay Spray', description: 'Mix wettable kaolin clay per label directions and apply from petal fall onwards, coating all fruit and foliage thoroughly. The fine particle film makes fruit surfaces unattractive for egg-laying and physically irritates larvae attempting to enter. Reapply every 7 - 10 days and after rain throughout the season.' },
      { name: 'Pheromone Traps for Monitoring', description: 'Hang one pheromone sticky trap per tree from petal fall. Traps capture male moths and let you time spray applications accurately. A trap catch of 5 or more moths per week is the action threshold for most organic programmes. Replace pheromone lures every 4 - 6 weeks.' },
      { name: 'Codling Moth Granulosis Virus (CpGV)', description: 'CpGV is a naturally occurring virus specific to codling moth larvae, approved for organic use. Apply as a spray every 7 - 10 days during egg hatch and larval entry periods (use pheromone traps to time this). Highly effective with regular applications; no impact on beneficial insects, birds, or mammals.' },
      { name: 'Corrugated Cardboard Bands', description: 'Wrap 15 cm wide bands of corrugated cardboard around the trunk in midsummer. Mature larvae leave fruit to pupate and will shelter in the flutes. Remove and destroy the bands every 2 - 3 weeks. In trials this can reduce populations by 30 - 50% when used consistently each season.' },
      { name: 'Remove Fallen Fruit Promptly', description: 'Collect and destroy all dropped fruit every 2 - 3 days throughout the season. Infested fruit contains maturing larvae that will go on to produce the next generation of adults. Do not add damaged fruit to a compost heap - bag it or submerge in water for at least a week first.' },
    ],
    preventionTips: [
      'Hang pheromone monitoring traps from petal fall every season - accurate population data allows precisely timed interventions rather than routine calendar spraying',
      'Apply kaolin clay from petal fall as a season-long barrier - starting early before egg-laying begins is more effective than responding to first damage',
      'Encourage tits, woodpeckers, and nuthatches with nest boxes and winter feeding - these birds forage in apple bark and remove overwintering pupae at a significant rate',
      'Collect and destroy windfall fruit every 2 - 3 days throughout summer and autumn - this single step meaningfully reduces the next generation of adults',
      'Avoid storing damaged or suspect fruit near healthy fruit - larvae continue to develop in storage and will exit to pupate, producing more adults',
    ],
  },

  {
    id: 'apple-maggot',
    image: '/images/pests/apple-maggot.png',
    name: 'Apple Maggot',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm', 'humid'],
    description: 'The larva of Rhagoletis pomonella, a small fly with banded wings. Adults emerge from mid-June onwards and females use a needle-like ovipositor to insert single eggs just under the fruit skin. Larvae are white, legless maggots 6 - 8 mm long that burrow through the flesh in winding tunnels, causing the fruit to rot from inside. Primarily a pest in eastern North America.',
    symptoms: [
      'Tiny puncture marks in the skin where eggs were inserted, sometimes with a slight dimple',
      'Winding brown streaks or corky tunnels through the flesh when the fruit is cut open',
      'Fruit feels soft in spots and rots prematurely, often while still on the tree',
      'Early fruit drop from late June through August, fruit apparently healthy on the outside',
      'Small white maggots found inside rotting fruit, most visible near the core',
    ],
    affectedPlants: ['Apple', 'Crabapple', 'Hawthorn', 'Cherry', 'Plum', 'Pear'],
    organicSolutions: [
      { name: 'Red Sphere Sticky Traps', description: 'Hang red sticky sphere traps (approximately 7 cm diameter) coated with Tanglefoot or similar adhesive from late June, one trap per dwarf tree, two to four per standard. Flies are attracted to the sphere thinking it is a fruit. Replace or re-coat when the surface is covered. Use traps both to monitor and to reduce adult populations.' },
      { name: 'Kaolin Clay Spray', description: 'Begin applying kaolin clay from late June when adults first emerge and continue every 7 - 10 days through August. Coat all fruit surfaces thoroughly. The particle film deters females from inserting eggs and makes fruit surfaces physically difficult to penetrate. Reapply after rain.' },
      { name: 'Exclusion Bags on Individual Fruit', description: 'After the June drop and once fruit has reached marble size, slip small organza or paper bags over individual fruits and secure. This physically prevents flies from reaching the fruit surface entirely. Effective for hobbyist growers with manageable crop sizes.' },
      { name: 'Remove Host Plants Nearby', description: 'Wild crabapples and hawthorns within 100 m are significant reservoirs of apple maggot fly. If possible, remove them or harvest fruit promptly to prevent adjacent populations building up and migrating to cultivated trees.' },
    ],
    preventionTips: [
      'Hang red sphere monitoring traps from mid-June every year - population data makes interventions far more precise than calendar timing',
      'Remove all fallen fruit within 2 - 3 days throughout the season - maggots in fallen fruit continue to develop and pupate in the soil beneath the tree',
      'Apply kaolin clay from late June as a season-long barrier - early, consistent coverage before peak egg-laying is significantly more effective than late intervention',
      'Check and bag individual fruit after June drop if the crop is small enough to manage - physical exclusion is close to 100% effective',
    ],
  },

  {
    id: 'woolly-apple-aphid',
    image: '/images/pests/woolly-apple-aphid.png',
    name: 'Woolly Apple Aphid',
    type: 'insect',
    tags: ['sticky', 'stunted', 'yellowing'],
    weatherTriggers: ['warm'],
    description: 'Eriosoma lanigerum is a reddish-purple aphid, 1 - 2 mm long, covered in dense white woolly wax it secretes for protection. It forms dense colonies on bark wounds, pruning cuts, and branch crotches aboveground, and on roots belowground. Root colonies cause the most serious long-term damage, forming knotty galls that disrupt water and nutrient uptake.',
    symptoms: [
      'Conspicuous patches of white, fluffy, cotton-like wool on bark, branch crotches, and pruning wounds',
      'Reddish-purple insects visible under the white wool when disturbed',
      'Sticky honeydew coating bark and leaves below colonies, with black sooty mould developing on it',
      'Knotty galls or swollen areas on the roots, visible when the soil around the base is loosened',
      'General decline, dieback of affected branches, or reduced vigour in heavily infested trees',
    ],
    affectedPlants: ['Apple', 'Crabapple', 'Pear', 'Hawthorn', 'Mountain Ash', 'Elm'],
    organicSolutions: [
      { name: 'Direct Alcohol Application', description: 'Dab colonies directly with a cotton bud or cloth soaked in 70% isopropyl alcohol or surgical spirit. The alcohol penetrates the woolly wax coating and kills the aphids on contact. Most practical for small colonies on accessible bark. Recheck after 10 days and repeat.' },
      { name: 'Insecticidal Soap or Neem Spray', description: 'Mix a 2% castile soap solution or neem oil spray and apply directly onto colonies at high pressure using a sprayer. Work the spray under the white wool coating. Multiple applications 5 - 7 days apart are needed to catch newly hatched crawlers that emerge from eggs protected under the colony mass.' },
      { name: 'Encourage Parasitic Wasps', description: 'Aphelinus mali, a tiny parasitic wasp, is a specific natural enemy of woolly apple aphid and provides significant biological control where it is established. Plant dill, fennel, phacelia, and yarrow nearby to support beneficial wasp populations. Avoid broad-spectrum insecticides that kill these natural enemies.' },
      { name: 'Scrub Bark Colonies', description: 'For accessible colonies on smooth young bark, scrub directly with a stiff brush soaked in soapy water. Remove loose bark, pruning stubs, and rough surfaces where colonies establish. Follow with a soap or neem spray to kill any remaining insects.' },
    ],
    preventionTips: [
      'Plant on woolly apple aphid-resistant rootstocks where available - MM.106 and MM.111 have moderate resistance and are widely available',
      'Prune cleanly and seal large wounds promptly - rough pruning cuts and old stubs are primary establishment sites for colonies',
      'Maintain a moat of bare soil around the trunk base rather than deep mulch directly against bark, which creates conditions where root colonies go unnoticed for longer',
      'Plant a diverse understorey of flowering herbs and perennials to sustain Aphelinus mali and other beneficial parasitic wasps year-round',
    ],
  },

  {
    id: 'san-jose-scale',
    image: '/images/pests/san-jose-scale.png',
    name: 'San Jose Scale',
    type: 'insect',
    tags: ['yellowing', 'stunted'],
    weatherTriggers: ['warm', 'humid'],
    description: 'Comstock\'s scale (Quadraspidiotus perniciosus) is a 1 - 2 mm armoured scale insect that encrusts bark, twigs, and developing fruit. The individual scale is circular, grey, and has a yellow or orange centre. It is barely visible individually but builds up to form bark-encrusting layers that sap tree vigour. Heavily infested trees decline over several seasons before the cause is identified.',
    symptoms: [
      'Grey, circular scale encrusting bark surfaces on twigs and branches, easily scraped off to reveal a yellow centre',
      'A faint red or purple halo discolouring fruit skin around each scale feeding point',
      'Premature leaf yellowing and shoot dieback that progresses over multiple seasons',
      'Bark of heavily infested branches appearing rough, greyish, and encrusted',
      'Reduced growth and general decline in tree vigour, particularly on young wood',
    ],
    affectedPlants: ['Apple', 'Pear', 'Peach', 'Cherry', 'Plum', 'Currant', 'Gooseberry', 'Raspberry'],
    organicSolutions: [
      { name: 'Dormant Oil Spray', description: 'Apply a refined horticultural dormant oil at the highest labelled rate in late winter or very early spring, before buds show any green. Thorough coverage of all bark surfaces smothers overwintering scales by sealing their air pores. This is the single most effective organic intervention for established scale populations.' },
      { name: 'Summer Oil Spray', description: 'A lighter summer oil application can be applied at crawler emergence (early to midsummer) when young scales are mobile and most vulnerable. Spray all bark and stem surfaces. Avoid applying in temperatures above 30 degrees or in full sun to prevent phytotoxicity.' },
      { name: 'Prune and Destroy Heavily Infested Wood', description: 'Cut out branches with heavy scale encrustation and remove from the site. This directly reduces the population and improves air circulation and light penetration, which create less favourable conditions for scale to persist. Disinfect pruning tools between cuts.' },
      { name: 'Encourage Predators', description: 'Parasitic wasps (Encarsia and Aphytis species) and predatory beetles naturally regulate scale populations. Avoid any broad-spectrum pesticides that disrupt these populations. Plant phacelia, yarrow, dill, and sweet alyssum to support beneficial insect populations through the season.' },
    ],
    preventionTips: [
      'Apply dormant oil spray every winter before bud break as a standard part of fruit tree care - consistent annual applications prevent populations building to damaging levels',
      'Inspect twigs and branches annually in late winter with a hand lens for early encrustation before populations are established',
      'Prune for open canopy structure to improve air circulation and reduce the humid still-air conditions that favour scale build-up',
      'Avoid nitrogen-heavy fertilising which produces soft sappy growth more susceptible to scale establishment',
    ],
  },

  {
    id: 'plum-curculio',
    image: '/images/pests/plum-curculio.png',
    name: 'Plum Curculio',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'Conotrachelus nenuphar is a small, mottled brown weevil, 5 - 6 mm long, with a distinctive curved snout and four bumps on its back. Adults emerge from overwintering sites around apple petal fall and feed on and lay eggs in young developing fruit. After egg-laying, the female cuts a crescent-shaped scar around each egg site, a distinctive identification mark. The larva develops inside the fruit, causing it to drop prematurely.',
    symptoms: [
      'Crescent-shaped or C-shaped scars cut into young fruit skin shortly after petal fall',
      'Small sunken feeding pits or dimples on fruit surface, sometimes oozing a small amount of juice',
      'Premature fruit drop in May and June, fruit noticeably small and hard inside',
      'White legless grubs found inside prematurely dropped fruit',
      'Misshapen, scarred, or corky areas on fruit that did not drop but carried eggs that hatched',
    ],
    affectedPlants: ['Apple', 'Pear', 'Peach', 'Cherry', 'Plum', 'Quince', 'Blueberry'],
    organicSolutions: [
      { name: 'Kaolin Clay Spray', description: 'Begin applying kaolin clay at first pink (before flowers open) and continue through the 4 - 6 weeks after petal fall when adults are most active and egg-laying. Coat all fruit and foliage thoroughly and reapply every 7 days and after rain. The particle film physically deters weevils from feeding and laying eggs on coated surfaces.' },
      { name: 'Trunk Tapping and Collection', description: 'Weevils drop from trees when disturbed. Lay a white sheet or tarpaulin under the tree and strike branches firmly with a padded mallet early in the morning when adults are sluggish with cold. Collect and destroy fallen weevils. Most effective in the 2 - 3 weeks after petal fall when adults are concentrating on young fruit.' },
      { name: 'Entomopathogenic Nematodes', description: 'Apply Steinernema carpocapsae or Heterorhabditis bacteriophora nematodes to soil under trees in midsummer when larvae and pupae are present in the top layer of soil. Keep soil moist for 2 weeks after application. Nematodes parasitise larvae and reduce the overwintering adult population for following seasons.' },
      { name: 'Remove Dropped Fruit', description: 'Collect and destroy all fallen fruit every 2 - 3 days from petal fall through June. Most dropped fruit contains larvae that will mature, burrow into soil, and pupate. Destroying fallen fruit is one of the highest-impact low-cost interventions available.' },
    ],
    preventionTips: [
      'Begin kaolin clay applications before flower opening rather than waiting for damage - adults arrive at petal fall and egg-laying starts immediately',
      'Collect and destroy all fallen fruit every 2 - 3 days from mid-May through June - this significantly reduces the soil-overwintering adult population for the following year',
      'Apply nematodes to the soil under trees in midsummer - consistent annual applications build up a persistent population of natural larval enemies in the soil',
      'Cultivate the soil under trees shallowly in late autumn to expose overwintering pupae to frost and birds',
    ],
  },

  {
    id: 'cutworms',
    image: '/images/pests/cutworms.png',
    name: 'Cutworms',
    type: 'insect',
    tags: ['holes', 'chewing', 'wilt'],
    weatherTriggers: ['wet-soil'],
    description: 'Fat, grey or brown caterpillars up to 5 cm long that curl into a C-shape when disturbed, found just below the soil surface near plant stems.',
    symptoms: [
      'Seedlings cut off cleanly at soil level overnight',
      'Young stems severed or tunnelled through near the base',
      'Plants collapsing suddenly despite moist soil',
    ],
    affectedPlants: ['Tomato', 'Pepper', 'Brassica', 'Lettuce', 'Corn', 'Bean', 'Pea', 'Carrot', 'Beetroot'],
    organicSolutions: [
      {
        name: 'Physical collars',
        description: 'Press a cardboard or plastic collar 2 - 3 cm into the soil around each transplant stem to block cutworm access.',
      },
      {
        name: 'Beneficial nematodes',
        description: 'Water Steinernema carpocapsae nematodes into warm moist soil around plant bases before planting.',
      },
      {
        name: 'Diatomaceous earth',
        description: 'Sprinkle food-grade diatomaceous earth in a ring on the soil surface around stems.',
      },
    ],
    preventionTips: [
      'Cultivate soil well before planting to expose and kill overwintering pupae',
      'Check soil at night with a torch to hand-pick larvae',
      'Delay transplanting until soil temperatures rise above 15 C to reduce larval activity',
    ],
  },

  {
    id: 'thrips',
    image: '/images/pests/thrips.png',
    name: 'Thrips',
    type: 'insect',
    tags: ['spots', 'yellowing', 'stunted', 'sticky'],
    weatherTriggers: ['warm', 'dry'],
    description: 'Tiny slender insects 1 - 2 mm long, straw-yellow to dark brown, that rasp leaf surfaces and feed on the cell contents, leaving a distinctive silvery sheen.',
    symptoms: [
      'Silver-grey streaks or papery patches on upper leaf surfaces',
      'Tiny black specks of frass on leaves',
      'Flowers and fruit with pale streaks, scarring, or distorted growth',
      'Stunted shoot tips with cupped or crinkled new leaves',
    ],
    affectedPlants: ['Onion', 'Leek', 'Allium', 'Pepper', 'Tomato', 'Strawberry', 'Rose', 'Bean', 'Pea', 'Cucumber', 'Courgette'],
    organicSolutions: [
      {
        name: 'Blue sticky traps',
        description: 'Hang blue sticky traps at crop height to monitor and reduce adult thrips populations.',
      },
      {
        name: 'Insecticidal soap spray',
        description: 'Spray a dilute insecticidal soap solution directly onto affected foliage, paying attention to undersides and leaf axils, every 5 - 7 days.',
      },
      {
        name: 'Predatory mites',
        description: 'Introduce Amblyseius cucumeris predatory mites onto plants to feed on young thrips larvae in protected growing environments.',
      },
    ],
    preventionTips: [
      'Remove and destroy heavily infested leaves immediately to reduce population spread',
      'Keep plants well watered during dry spells as drought stress worsens thrips damage',
      'Avoid overhead watering once plants are established - wet foliage encourages fungal disease but does not deter thrips',
    ],
  },

  {
    id: 'squash-vine-borer',
    image: '/images/pests/squash-vine-borer.png',
    name: 'Squash Vine Borer',
    type: 'insect',
    tags: ['wilt', 'holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'White, fat caterpillars up to 2.5 cm long found inside squash stems; the adult is a wasp-mimicking moth with an orange and black abdomen.',
    symptoms: [
      'Sudden wilting of one or more vines even with adequate water',
      'Sawdust-like frass at the base of wilting stems',
      'Entry holes and tunnels visible when stems are cut lengthwise',
    ],
    affectedPlants: ['Squash', 'Courgette', 'Pumpkin', 'Marrow'],
    organicSolutions: [
      {
        name: 'Row covers',
        description: 'Protect plants with fine mesh row covers from transplanting until flowers open, removing for pollination, to exclude egg-laying adults.',
      },
      {
        name: 'Manual removal',
        description: 'Slit infested stems lengthwise, remove larvae by hand, and bury the stem segment under moist soil to encourage rooting above the damage.',
      },
      {
        name: 'Bacillus thuringiensis injection',
        description: 'Inject Bt solution into the stem with a syringe at entry holes when larvae are young and still near the surface.',
      },
    ],
    preventionTips: [
      'Delay planting squash until after the first adult moth flight in early summer',
      'Wrap the lower 30 cm of stems in foil to deter egg-laying near the crown',
      'Check stems weekly from late June for orange oval eggs and crush them before hatch',
    ],
  },

  {
    id: 'corn-earworm',
    image: '/images/pests/corn-earworm.png',
    name: 'Corn Earworm',
    type: 'insect',
    tags: ['holes', 'chewing', 'fruit-damage'],
    weatherTriggers: ['warm'],
    description: 'Stout caterpillars up to 4 cm long, variable in colour from pale green to brown or reddish, found feeding inside corn ears, tomato fruits, and pepper pods.',
    symptoms: [
      'Silks clipped and eaten at the corn ear tip',
      'Frass-filled tunnels inside corn ears or tomato fruits',
      'Entry holes at the stem end of tomatoes or the tip of corn ears',
    ],
    affectedPlants: ['Corn', 'Tomato', 'Pepper', 'Bean', 'Squash', 'Pea'],
    organicSolutions: [
      {
        name: 'Mineral oil in silks',
        description: 'Apply a few drops of food-grade mineral oil to corn silks at the tip of the ear right after pollination to suffocate newly hatched larvae.',
      },
      {
        name: 'Bacillus thuringiensis spray',
        description: 'Spray Bt onto corn silks every 3 - 5 days during silk emergence to kill newly hatched larvae before they enter the ear.',
      },
      {
        name: 'Trichogramma wasps',
        description: 'Release Trichogramma egg-parasitic wasps at the start of silking to parasitise earworm eggs before they hatch.',
      },
    ],
    preventionTips: [
      'Plant sweet corn varieties with tight husk tips that physically deter larval entry',
      'Monitor with pheromone traps to time Bt applications to coincide with peak egg-laying',
      'Remove and destroy crop debris promptly after harvest to reduce overwintering pupae',
    ],
  },

  {
    id: 'stink-bug',
    image: '/images/pests/stink-bug.png',
    name: 'Stink Bug',
    type: 'insect',
    tags: ['spots', 'fruit-damage', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'Shield-shaped bugs 12 - 17 mm long, mottled brown or green, that pierce plant tissue and inject saliva, causing corky or dimpled damage to fruit.',
    symptoms: [
      'Dimpled, pitted, or corky patches on fruit surfaces',
      'Pithy white areas just beneath fruit skin',
      'Cat-facing deformity on tomatoes and peppers near the stem',
    ],
    affectedPlants: ['Tomato', 'Pepper', 'Apple', 'Peach', 'Pear', 'Bean', 'Corn', 'Strawberry', 'Raspberry'],
    organicSolutions: [
      {
        name: 'Hand removal',
        description: 'Knock bugs into a bucket of soapy water in the morning when they are sluggish - wear gloves to avoid the odour.',
      },
      {
        name: 'Kaolin clay',
        description: 'Spray kaolin clay suspension onto fruit and foliage to create a physical barrier that irritates and deters feeding adults.',
      },
      {
        name: 'Row covers',
        description: 'Cover small crops or individual fruit clusters with fine mesh bags or row cover fabric to exclude bugs during the risk period.',
      },
    ],
    preventionTips: [
      'Check undersides of leaves for bronze egg masses and crush them as soon as found',
      'Seal gaps in greenhouse and shed walls in autumn to prevent overwintering indoors',
      'Plant trap crops such as sunflowers or mustard at the garden perimeter to draw bugs away from crops',
    ],
  },

  {
    id: 'leafhoppers',
    image: '/images/pests/leafhoppers.png',
    name: 'Leafhoppers',
    type: 'insect',
    tags: ['yellowing', 'spots', 'stunted'],
    weatherTriggers: ['warm', 'dry'],
    description: 'Small wedge-shaped insects 3 - 6 mm long, pale green to yellow, that jump rapidly when disturbed and feed by sucking sap from leaf undersides.',
    symptoms: [
      'Pale stippled or bleached speckling across upper leaf surfaces',
      'Whitish cast skins on leaf undersides',
      'Curled, distorted, or yellowing leaf margins',
      'Stunted new growth on heavily infested plants',
    ],
    affectedPlants: ['Potato', 'Bean', 'Brassica', 'Strawberry', 'Lettuce', 'Carrot', 'Beetroot', 'Apple', 'Grape'],
    organicSolutions: [
      {
        name: 'Insecticidal soap spray',
        description: 'Spray insecticidal soap solution onto leaf undersides where nymphs congregate, repeating every 5 - 7 days during hot dry spells.',
      },
      {
        name: 'Reflective mulch',
        description: 'Lay silver reflective mulch on the soil surface around plants to disorient and deter adult leafhoppers from landing.',
      },
      {
        name: 'Kaolin clay',
        description: 'Apply kaolin clay spray to foliage to create a physical deterrent that reduces adult feeding and egg-laying.',
      },
    ],
    preventionTips: [
      'Remove weeds around the garden perimeter which serve as overwintering habitat and early-season host plants',
      'Use row covers on susceptible crops during peak leafhopper season',
      'Monitor with yellow sticky traps to track population build-up before damage becomes severe',
    ],
  },

  {
    id: 'root-maggot',
    image: '/images/pests/root-maggot.png',
    name: 'Root Maggot',
    type: 'insect',
    tags: ['wilt', 'stunted', 'yellowing'],
    weatherTriggers: ['cool', 'wet-soil'],
    description: 'Small white legless fly larvae 6 - 8 mm long found tunnelling through roots and bulbs; adults resemble small grey houseflies and lay eggs at plant bases.',
    symptoms: [
      'Plants wilting and yellowing despite adequate moisture',
      'Roots and bulbs tunnelled with dark slimy channels',
      'Plants lifting out of the soil with no root system remaining',
    ],
    affectedPlants: ['Brassica', 'Radish', 'Turnip', 'Onion', 'Leek', 'Carrot', 'Beetroot'],
    organicSolutions: [
      {
        name: 'Brassica collars',
        description: 'Place a 12 cm square of carpet underlay or felt flat on the soil around each brassica stem to prevent adults laying eggs at the soil surface.',
      },
      {
        name: 'Beneficial nematodes',
        description: 'Apply Steinernema feltiae nematodes to moist soil at transplanting time and again 3 weeks later to control larvae in the root zone.',
      },
      {
        name: 'Row covers',
        description: 'Drape fine mesh insect-proof netting over crops from sowing to exclude egg-laying adults completely.',
      },
    ],
    preventionTips: [
      'Rotate brassica and allium crops to a fresh bed each year to break the soil-based lifecycle',
      'Avoid planting brassicas during the peak adult fly period in spring - wait until late May in most regions',
      'Cultivate soil deeply after harvest to expose overwintering pupae to frost and predatory birds',
    ],
  },

  {
    id: 'wireworm',
    image: '/images/pests/wireworm.png',
    name: 'Wireworm',
    type: 'insect',
    tags: ['wilt', 'stunted', 'holes'],
    weatherTriggers: ['cool', 'wet-soil'],
    description: 'Shiny orange-brown, tough-skinned beetle larvae up to 25 mm long found tunnelling through roots, tubers, and bulbs in the soil.',
    symptoms: [
      'Seedlings collapsing at soil level with roots tunnelled through',
      'Potato tubers with round entry holes and internal tunnels',
      'Irregular wilting and death of scattered plants across a bed',
    ],
    affectedPlants: ['Potato', 'Carrot', 'Beetroot', 'Onion', 'Corn', 'Bean', 'Brassica', 'Lettuce'],
    organicSolutions: [
      {
        name: 'Trap baiting',
        description: 'Bury halved potato pieces on skewers 10 cm deep at 1 m intervals; check every 2 - 3 days and destroy any wireworms found.',
      },
      {
        name: 'Cultivation',
        description: 'Dig and fork beds frequently in late summer and autumn to expose wireworms to birds and desiccation.',
      },
      {
        name: 'Beneficial nematodes',
        description: 'Apply Steinernema kraussei nematodes to moist soil in late summer to target young wireworm larvae before they burrow deep for winter.',
      },
    ],
    preventionTips: [
      'Avoid planting root crops in newly broken grassland where wireworm populations are highest',
      'Follow with a green manure and thorough cultivation for a full season before planting susceptible crops',
      'Harvest potato tubers as soon as mature to reduce exposure time in the ground',
    ],
  },

  {
    id: 'root-knot-nematode',
    image: '/images/pests/root-knot-nematode.png',
    name: 'Root Knot Nematode',
    type: 'other',
    tags: ['wilt', 'stunted', 'yellowing'],
    weatherTriggers: ['warm', 'wet-soil'],
    description: 'Microscopic roundworms that invade root tissue and cause the plant to form swollen galls, disrupting water and nutrient uptake throughout the whole plant.',
    symptoms: [
      'Irregular swellings or galls on roots ranging from pea-sized to large knotted masses',
      'Persistent wilting during hot weather despite moist soil',
      'Yellowing, stunting, and poor cropping with no obvious above-ground cause',
    ],
    affectedPlants: ['Tomato', 'Pepper', 'Cucumber', 'Bean', 'Carrot', 'Beetroot', 'Lettuce', 'Potato', 'Strawberry'],
    organicSolutions: [
      {
        name: 'Resistant varieties',
        description: 'Choose tomato and pepper varieties with N (nematode) resistance ratings in the seed catalogue - these prevent reproduction and protect yield.',
      },
      {
        name: 'Marigold cover crop',
        description: 'Grow Tagetes patula (French marigold) as a dense cover crop for a full season before planting susceptible crops - root exudates suppress nematode populations significantly.',
      },
      {
        name: 'Soil solarisation',
        description: 'Cover moist, cultivated soil with clear plastic for 6 - 8 weeks during the hottest summer months to heat the top 30 cm to nematode-lethal temperatures.',
      },
    ],
    preventionTips: [
      'Practice strict crop rotation, keeping records of where susceptible crops grew in previous years',
      'Avoid moving soil from infested beds on tools or boots',
      'Incorporate plenty of well-made compost to encourage beneficial soil organisms that compete with nematodes',
    ],
  },

  {
    id: 'leaf-miners',
    image: '/images/pests/leaf-miners.png',
    name: 'Leaf Miners',
    type: 'insect',
    tags: ['spots', 'holes', 'stunted'],
    description: 'Larvae of small flies or moths that tunnel between upper and lower leaf surfaces, leaving visible winding or blotch-shaped pale trails inside the leaf.',
    symptoms: [
      'Winding pale serpentine or blotch-shaped mines visible inside leaves when held to the light',
      'White papery patches where the larva has consumed leaf tissue',
      'Heavy infestations causing leaves to dry out and drop early',
    ],
    affectedPlants: ['Brassica', 'Spinach', 'Chard', 'Beetroot', 'Celery', 'Leek', 'Tomato', 'Pepper', 'Bean', 'Pea'],
    organicSolutions: [
      {
        name: 'Manual removal',
        description: 'Crush larvae inside mines by pinching the affected leaf tissue between fingers, or remove and destroy badly mined leaves.',
      },
      {
        name: 'Row covers',
        description: 'Protect susceptible crops with fine insect mesh from germination onwards to exclude adult flies before they can lay eggs.',
      },
      {
        name: 'Yellow sticky traps',
        description: 'Hang yellow sticky traps among crops to monitor and trap adult flies before egg-laying begins.',
      },
    ],
    preventionTips: [
      'Remove crop debris promptly as pupae overwinter in soil and old plant material',
      'Avoid growing susceptible crops in the same beds year after year',
      'Inspect young transplants carefully before planting to avoid introducing mined leaves',
    ],
  },

  {
    id: 'european-corn-borer',
    image: '/images/pests/european-corn-borer.png',
    name: 'European Corn Borer',
    type: 'insect',
    tags: ['holes', 'chewing', 'wilt'],
    weatherTriggers: ['warm'],
    description: 'Cream-coloured caterpillars up to 25 mm long with brown head capsules that bore into corn stalks, pepper stems, and other thick-stemmed plants.',
    symptoms: [
      'Frass-filled holes in corn stalks or pepper stems',
      'Stalks breaking over at bore sites in wind',
      'Dead tassels or ears at the point of larval entry',
    ],
    affectedPlants: ['Corn', 'Pepper', 'Bean', 'Potato', 'Tomato'],
    organicSolutions: [
      {
        name: 'Bacillus thuringiensis spray',
        description: 'Apply Bt spray into the corn whorl and onto leaf axils when young larvae are actively feeding on leaf surfaces before burrowing in.',
      },
      {
        name: 'Trichogramma wasps',
        description: 'Release Trichogramma parasitic wasps at first moth flight to parasitise freshly laid eggs on leaf undersides.',
      },
      {
        name: 'Manual removal',
        description: 'Slit infested stems at bore entry points, remove caterpillars by hand, and seal the wound with moist soil or tape.',
      },
    ],
    preventionTips: [
      'Shred and compost or remove all corn stalks promptly after harvest to destroy overwintering larvae',
      'Plant early-maturing corn varieties to reduce exposure to second-generation moths',
      'Monitor with pheromone traps to time Bt applications accurately to the egg-hatch window',
    ],
  },

  {
    id: 'cabbage-looper',
    image: '/images/pests/cabbage-looper.png',
    name: 'Cabbage Looper',
    type: 'insect',
    tags: ['holes', 'chewing', 'yellowing'],
    weatherTriggers: ['warm'],
    description: 'Light green caterpillars up to 4 cm long that arch their body into a loop as they walk; they feed voraciously on brassica leaves, often leaving only the midribs.',
    symptoms: [
      'Large ragged holes in brassica leaves, often starting in the leaf centre',
      'Leaves skeletonised to the midrib on heavily infested plants',
      'Green frass pellets on leaves and in the soil below plants',
    ],
    affectedPlants: ['Brassica', 'Lettuce', 'Spinach', 'Pea', 'Tomato', 'Celery'],
    organicSolutions: [
      {
        name: 'Bacillus thuringiensis spray',
        description: 'Spray Bt onto both leaf surfaces at first sign of young caterpillars, repeating every 5 - 7 days and after rain, for effective larval control.',
      },
      {
        name: 'Hand-picking',
        description: 'Inspect the undersides of leaves in the morning and remove caterpillars by hand, dropping them into soapy water.',
      },
      {
        name: 'Row covers',
        description: 'Drape lightweight insect mesh over brassica crops from transplanting to prevent adult moths from laying eggs on leaves.',
      },
    ],
    preventionTips: [
      'Check under leaves regularly for pale green ribbed oval eggs and crush them before hatch',
      'Encourage wasps and ground beetles by maintaining diverse plantings near the vegetable garden',
      'Plant a succession of brassicas to stagger the risk period and avoid all crops being in a vulnerable stage simultaneously',
    ],
  },

  {
    id: 'allium-leaf-miner',
    image: '/images/pests/allium-leaf-miner.png',
    name: 'Allium Leaf Miner',
    type: 'insect',
    tags: ['holes', 'spots', 'stunted'],
    weatherTriggers: ['cool'],
    description: 'A small fly whose larvae mine through leek and onion leaves and pupate inside the stems, leaving distinctive white puncture marks and causing stems to rot.',
    symptoms: [
      'Rows of white oval puncture marks along the length of leaves, made by females feeding',
      'Pale winding mines inside leaves',
      'Stems splitting open to reveal cream pupae in spring and autumn',
    ],
    affectedPlants: ['Leek', 'Onion', 'Allium', 'Garlic', 'Shallot', 'Chive'],
    organicSolutions: [
      {
        name: 'Insect mesh',
        description: 'Cover allium crops with fine insect-proof netting during the two adult flight periods in March - April and October - November.',
      },
      {
        name: 'Removal of infested plants',
        description: 'Pull and destroy heavily infested plants immediately to prevent pupae completing development in the soil.',
      },
    ],
    preventionTips: [
      'Rotate allium crops to a new bed each year to reduce local fly populations',
      'Avoid growing alliums adjacent to last year\'s allium bed',
      'Check plants regularly during flight periods for the characteristic puncture marks',
    ],
  },

  {
    id: 'asparagus-beetle',
    image: '/images/pests/asparagus-beetle.png',
    name: 'Asparagus Beetle',
    type: 'insect',
    tags: ['holes', 'chewing', 'yellowing'],
    weatherTriggers: ['warm'],
    description: 'Distinctive black beetles 6 - 8 mm long with cream-and-red spotted wing cases, and their grey grubs, that strip asparagus foliage from spring through autumn.',
    symptoms: [
      'Foliage stripped from spears or fern, leaving bare stems',
      'Grey grubs up to 10 mm long on stems and leaves',
      'Spears scarred with dark feeding scars from adult beetles',
    ],
    affectedPlants: ['Asparagus'],
    organicSolutions: [
      {
        name: 'Hand-picking',
        description: 'Check plants daily from spear emergence and pick adults and grubs into soapy water - early intervention prevents severe defoliation.',
      },
      {
        name: 'Encourage natural enemies',
        description: 'Avoid disturbing ground beetles and parasitic wasps that prey on asparagus beetle larvae and pupae in the soil.',
      },
    ],
    preventionTips: [
      'Clear and compost all fern growth in autumn to remove overwintering adult habitat',
      'Cultivate the soil around crowns in late winter to expose overwintering adults to frost and birds',
    ],
  },

  {
    id: 'bean-leaf-beetle',
    image: '/images/pests/bean-leaf-beetle.png',
    name: 'Bean Leaf Beetle',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'Small oval beetles about 6 mm long, variable in colour from red to tan or yellow, that chew circular holes in bean leaves and sometimes feed on pods.',
    symptoms: [
      'Circular holes scattered across bean leaves',
      'Pod scarring and entry holes on developing bean pods',
      'Reduced pod fill and quality on heavily attacked plants',
    ],
    affectedPlants: ['Bean', 'Soybean', 'Pea'],
    organicSolutions: [
      {
        name: 'Row covers',
        description: 'Cover bean crops from planting with fine insect mesh and remove only for pollination to exclude egg-laying beetles.',
      },
      {
        name: 'Hand removal',
        description: 'Shake plants gently over a sheet in the morning and collect beetles that drop off.',
      },
    ],
    preventionTips: [
      'Delay planting until soil has fully warmed to give plants vigorous growth ahead of beetle populations',
      'Rotate beans to different beds each season',
    ],
  },

  {
    id: 'blueberry-maggot',
    image: '/images/pests/blueberry-maggot.png',
    name: 'Blueberry Maggot',
    type: 'insect',
    tags: ['fruit-damage'],
    weatherTriggers: ['warm'],
    description: 'Larvae of a small fly that infest blueberry fruits, causing them to shrivel and drop prematurely; infested berries look normal externally until squeezed.',
    symptoms: [
      'Fruit shrivelling and dropping before full ripeness',
      'White maggots inside berries when cut open',
      'Soft watery spots on otherwise normal-looking fruit',
    ],
    affectedPlants: ['Blueberry'],
    organicSolutions: [
      {
        name: 'Red sphere traps',
        description: 'Hang sticky red sphere traps baited with ammonium acetate in bushes from early July to monitor and trap adult flies before egg-laying.',
      },
      {
        name: 'Kaolin clay spray',
        description: 'Apply kaolin clay suspension to developing fruit clusters to deter adult flies from laying eggs on berries.',
      },
    ],
    preventionTips: [
      'Pick berries as soon as ripe to reduce the number available for infestation',
      'Remove and destroy all dropped fruit promptly to prevent larvae entering the soil to pupate',
    ],
  },

  {
    id: 'carrot-fly',
    image: '/images/pests/carrot-fly.png',
    name: 'Carrot Fly',
    type: 'insect',
    tags: ['wilt', 'stunted', 'yellowing'],
    weatherTriggers: ['cool'],
    description: 'A small black fly whose cream larvae tunnel through carrot roots from the outside in, creating rusty brown tunnels and enabling rot to set in.',
    symptoms: [
      'Rusty brown tunnels in the outer carrot flesh when roots are pulled',
      'Foliage turning red-bronze and wilting in older plants',
      'Seedlings collapsing as roots are destroyed underground',
    ],
    affectedPlants: ['Carrot', 'Parsnip', 'Celery', 'Parsley'],
    organicSolutions: [
      {
        name: 'Physical barriers',
        description: 'Erect a 60 cm tall barrier of fine insect mesh or fleece around carrot beds - adults fly low and are stopped by the fence.',
      },
      {
        name: 'Insect mesh cover',
        description: 'Cover carrot rows with fine insect-proof netting from sowing through to harvest to exclude egg-laying females completely.',
      },
    ],
    preventionTips: [
      'Avoid thinning carrots in the evening when the fly is most active - crush thinnings to minimise smell',
      'Grow resistant varieties such as Flyaway or Resistafly which are significantly less attractive to females',
      'Sow after late May to miss the main spring generation, or sow under cover and transplant carefully',
    ],
  },

  {
    id: 'cherry-fruit-fly',
    image: '/images/pests/cherry-fruit-fly.png',
    name: 'Cherry Fruit Fly',
    type: 'insect',
    tags: ['fruit-damage'],
    weatherTriggers: ['warm'],
    description: 'A small banded-wing fly whose larvae tunnel inside cherry fruits, causing premature drop; infested cherries often look sound until cut or squeezed.',
    symptoms: [
      'Early fruit drop with maggots visible inside fallen cherries',
      'Softening and browning around the stone',
      'White larvae up to 6 mm long inside ripe or ripening fruit',
    ],
    affectedPlants: ['Cherry'],
    organicSolutions: [
      {
        name: 'Yellow sticky traps',
        description: 'Hang yellow sticky traps in cherry trees from late May to monitor adult emergence and time any treatments accurately.',
      },
      {
        name: 'Fine mesh netting',
        description: 'Cover smaller trees or individual branches with fine fruit fly exclusion netting before adults emerge to physically prevent egg-laying.',
      },
    ],
    preventionTips: [
      'Collect and destroy all fallen fruit immediately - do not compost as larvae will continue development in the pile',
      'Cultivate the soil beneath trees in autumn and winter to expose overwintering pupae to birds and frost',
    ],
  },

  {
    id: 'citrus-leafminer',
    image: '/images/pests/citrus-leafminer.png',
    name: 'Citrus Leafminer',
    type: 'insect',
    tags: ['spots', 'holes', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'A tiny moth whose larvae mine silvery serpentine trails into young citrus leaves, causing them to curl and distort as the tissue is consumed.',
    symptoms: [
      'Silver shiny winding trails clearly visible inside curled young leaves',
      'Leaves curling and twisting inward along the mine',
      'Stunted, distorted flushes of new growth on young trees',
    ],
    affectedPlants: ['Lemon', 'Orange', 'Lime', 'Citrus'],
    organicSolutions: [
      {
        name: 'Neem oil spray',
        description: 'Spray neem oil solution onto all new flush growth every 7 - 10 days during active growth periods to deter egg-laying females.',
      },
      {
        name: 'Pheromone traps',
        description: 'Use pheromone traps to monitor male moth activity and time neem sprays to coincide with peak flight periods.',
      },
    ],
    preventionTips: [
      'Avoid excessive nitrogen fertilising which promotes the soft new flushes that adults prefer for egg-laying',
      'Remove and destroy heavily mined leaves to reduce the local larval population',
    ],
  },

  {
    id: 'grape-berry-moth',
    image: '/images/pests/grape-berry-moth.png',
    name: 'Grape Berry Moth',
    type: 'insect',
    tags: ['fruit-damage', 'holes'],
    weatherTriggers: ['warm'],
    description: 'A small tortricid moth whose larvae attack grape clusters in three generations, first binding flower parts, then boring into green berries, then feeding in ripe fruit.',
    symptoms: [
      'Webbing binding flowers or young berries together in the cluster',
      'Green berries with entry holes turning blue-black prematurely',
      'Ripe berries shrivelling with frass-filled tunnels inside',
    ],
    affectedPlants: ['Grape'],
    organicSolutions: [
      {
        name: 'Bacillus thuringiensis spray',
        description: 'Apply Bt spray to grape clusters at each generation emergence, timed using degree-day models or pheromone trap catches.',
      },
      {
        name: 'Pheromone traps',
        description: 'Hang pheromone traps in the vineyard from May to track adult flight and time Bt applications to larval hatch.',
      },
    ],
    preventionTips: [
      'Remove and destroy mummified berries hanging in the vine during dormant pruning',
      'Strip loose bark from trunks and cordons in winter to destroy overwintering pupae',
    ],
  },

  {
    id: 'harlequin-bug',
    image: '/images/pests/harlequin-bug.png',
    name: 'Harlequin Bug',
    type: 'insect',
    tags: ['spots', 'yellowing', 'wilt'],
    weatherTriggers: ['warm', 'hot'],
    description: 'Brightly coloured red and black shield bugs about 9 mm long that inject toxic saliva into brassica tissue as they feed, causing wilting and white blistered patches.',
    symptoms: [
      'White or yellow wilted patches on leaves spreading from feeding sites',
      'Plants collapsing suddenly during hot weather',
      'Clusters of red and black eggs on leaf undersides',
    ],
    affectedPlants: ['Brassica', 'Mustard'],
    organicSolutions: [
      {
        name: 'Hand removal',
        description: 'Collect adults and egg masses by hand in the morning when bugs are slower, dropping them into a bucket of soapy water.',
      },
      {
        name: 'Trap crop',
        description: 'Plant a border of mustard or arugula to attract bugs away from main crops, then destroy the trap crop with insects on it.',
      },
    ],
    preventionTips: [
      'Clear brassica debris promptly after harvest as adults overwinter under plant material',
      'Check leaf undersides for distinctive red-and-black barrel-shaped egg masses and crush them immediately',
    ],
  },

  {
    id: 'mexican-bean-beetle',
    image: '/images/pests/mexican-bean-beetle.png',
    name: 'Mexican Bean Beetle',
    type: 'insect',
    tags: ['holes', 'chewing', 'yellowing'],
    weatherTriggers: ['warm'],
    description: 'A copper-coloured ladybug-like beetle with 16 black spots; both adults and yellow spiny larvae skeletonise bean leaf undersides, leaving only the veins.',
    symptoms: [
      'Leaf undersides skeletonised, leaving a lacy pattern of veins',
      'Yellow spiny oval larvae clustered on leaf undersides',
      'Leaves turning brown and papery where feeding has occurred',
    ],
    affectedPlants: ['Bean', 'Soybean'],
    organicSolutions: [
      {
        name: 'Neem oil spray',
        description: 'Spray neem oil solution onto leaf undersides where larvae feed, repeating every 7 days through the season.',
      },
      {
        name: 'Hand-picking',
        description: 'Remove yellow egg clusters from leaf undersides and crush larvae by hand during morning inspections.',
      },
      {
        name: 'Parasitic wasps',
        description: 'Release Pediobius foveolatus parasitic wasps to target larvae - highly effective when released during warm settled weather.',
      },
    ],
    preventionTips: [
      'Clear bean debris immediately after harvest to remove overwintering habitat',
      'Plant a trap crop of soybeans at the garden edge to draw beetles away from snap beans',
    ],
  },

  {
    id: 'onion-fly',
    image: '/images/pests/onion-fly.png',
    name: 'Onion Fly',
    type: 'insect',
    tags: ['wilt', 'yellowing', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'A grey fly resembling a small housefly whose larvae tunnel into the base of onion bulbs and roots, causing plants to collapse and rot.',
    symptoms: [
      'Young plants wilting and yellowing at the tips from the seedling stage',
      'Plants lifting out of the ground with the bulb rotted away',
      'White maggots feeding inside bulbs at soil level',
    ],
    affectedPlants: ['Onion', 'Shallot', 'Garlic', 'Leek'],
    organicSolutions: [
      {
        name: 'Insect mesh cover',
        description: 'Cover allium crops with fine insect netting from sowing or planting through to harvest to exclude egg-laying adults.',
      },
      {
        name: 'Grow from sets',
        description: 'Plant onion sets rather than sowing from seed - sets establish quickly and are less vulnerable during the most susceptible seedling stage.',
      },
    ],
    preventionTips: [
      'Rotate alliums to fresh ground each year as pupae overwinter in the soil',
      'Remove and destroy any infested plants immediately to prevent maggots completing development',
    ],
  },

  {
    id: 'oriental-fruit-moth',
    image: '/images/pests/oriental-fruit-moth.png',
    name: 'Oriental Fruit Moth',
    type: 'insect',
    tags: ['wilt', 'fruit-damage', 'holes'],
    weatherTriggers: ['warm'],
    description: 'A small grey moth whose larvae attack shoot tips in spring and then bore into developing fruit from early summer onwards, causing them to drop or rot.',
    symptoms: [
      'Wilting and death of new shoot tips with larvae visible inside',
      'Frass-plugged entry holes in fruit near the stem end',
      'Internal tunnelling and browning around the stone in stone fruits',
    ],
    affectedPlants: ['Peach', 'Nectarine', 'Plum', 'Apple', 'Pear', 'Cherry', 'Quince'],
    organicSolutions: [
      {
        name: 'Pheromone mating disruption',
        description: 'Hang pheromone mating disruption ties throughout the tree canopy at petal fall to confuse males and prevent mating across a whole orchard.',
      },
      {
        name: 'Bacillus thuringiensis spray',
        description: 'Apply Bt spray at egg hatch (timed by pheromone trap peaks) to kill young larvae before they enter the fruit.',
      },
    ],
    preventionTips: [
      'Thin fruit clusters to single fruits to reduce the probability of larval transfer between touching fruits',
      'Collect and destroy all dropped fruit every few days during the season',
    ],
  },

  {
    id: 'pear-psylla',
    image: '/images/pests/pear-psylla.png',
    name: 'Pear Psylla',
    type: 'insect',
    tags: ['sticky', 'yellowing', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'Tiny jumping insects 2 - 3 mm long that produce large quantities of sticky honeydew on pear leaves, promoting sooty mould and weakening the tree.',
    symptoms: [
      'Leaves coated in sticky honeydew, blackening with sooty mould',
      'Premature leaf drop from heavily infested shoots',
      'Stunted, distorted new growth and reduced fruit size',
    ],
    affectedPlants: ['Pear'],
    organicSolutions: [
      {
        name: 'Dormant oil spray',
        description: 'Apply horticultural winter oil spray to all bark surfaces during dormancy to smother overwintering adults before egg-laying begins.',
      },
      {
        name: 'Kaolin clay spray',
        description: 'Apply kaolin clay to all foliage from bud swell through fruit set to deter adult feeding and egg-laying.',
      },
      {
        name: 'Predatory insects',
        description: 'Encourage lacewings and ladybirds which prey on psylla nymphs - avoid broad-spectrum sprays that would eliminate these natural enemies.',
      },
    ],
    preventionTips: [
      'Prune out dense interior growth to improve air circulation and reduce humidity in the canopy',
      'Wash trees with a strong jet of water in spring to dislodge newly hatched nymphs',
    ],
  },

  {
    id: 'phylloxera',
    image: '/images/pests/phylloxera.png',
    name: 'Grape Phylloxera',
    type: 'insect',
    tags: ['yellowing', 'wilt', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'Microscopic aphid-like insects that attack grape roots and sometimes leaves, forming galls and disrupting nutrient uptake, causing progressive vine decline.',
    symptoms: [
      'Gradual decline in vine vigour over several seasons',
      'Yellow or reddish galls on leaf undersides on susceptible varieties',
      'Root galls and rotting of fine feeder roots visible on excavation',
    ],
    affectedPlants: ['Grape'],
    organicSolutions: [
      {
        name: 'Resistant rootstocks',
        description: 'Graft vines onto resistant American rootstocks which tolerate phylloxera feeding on roots without fatal damage - the only reliable long-term approach.',
      },
      {
        name: 'Sandy soil culture',
        description: 'Plant vines in sandy soils where phylloxera movement is more difficult and populations build up more slowly than in clay soils.',
      },
    ],
    preventionTips: [
      'Never move soil, tools, or plant material from a known infested vineyard without decontamination',
      'Inspect new vine purchases for root galls before planting',
    ],
  },

  {
    id: 'raspberry-beetle',
    image: '/images/pests/raspberry-beetle.png',
    name: 'Raspberry Beetle',
    type: 'insect',
    tags: ['fruit-damage', 'holes'],
    weatherTriggers: ['warm'],
    description: 'A small brown beetle 4 mm long whose cream larvae feed inside raspberry, blackberry, and loganberry fruits, making them unpalatable at harvest.',
    symptoms: [
      'Cream grubs found inside fruits at picking',
      'Ripe fruits drying out or collapsing at the core',
      'Small entry holes at the stalk end of ripening fruits',
    ],
    affectedPlants: ['Raspberry', 'Blackberry', 'Loganberry'],
    organicSolutions: [
      {
        name: 'Encourage birds',
        description: 'Adult beetles overwinter in the soil under canes - disturb the soil regularly in winter to expose them to robins and thrushes.',
      },
      {
        name: 'Cultivation under canes',
        description: 'Fork the soil shallowly under canes in late winter and again after harvest to expose pupae to desiccation and predators.',
      },
    ],
    preventionTips: [
      'Pick fruits as soon as ripe to reduce the number of larvae completing development inside harvested fruit',
      'Cultivate the soil under canes in autumn to disrupt the overwintering pupae',
    ],
  },

  {
    id: 'sawfly',
    image: '/images/pests/sawfly.png',
    name: 'Sawfly',
    type: 'insect',
    tags: ['holes', 'chewing', 'yellowing'],
    weatherTriggers: ['warm'],
    description: 'Wasp-like insects whose caterpillar-like larvae strip gooseberry, currant, and other woody plant foliage, sometimes defoliating entire plants within days.',
    symptoms: [
      'Leaves stripped from the inside of bushes outward, starting from low on the plant',
      'Pale green larvae with black spots clustered on leaf undersides',
      'Complete defoliation of susceptible plants in severe infestations',
    ],
    affectedPlants: ['Gooseberry', 'Currant', 'Apple', 'Plum', 'Rose'],
    organicSolutions: [
      {
        name: 'Hand-picking',
        description: 'Inspect the inner branches of gooseberry and currant bushes weekly from April and remove larvae by hand before populations build up.',
      },
      {
        name: 'Insecticidal soap spray',
        description: 'Spray insecticidal soap solution onto larvae on leaf undersides as soon as colonies are spotted, before they disperse through the plant.',
      },
    ],
    preventionTips: [
      'Check the inside of bushes carefully at least weekly from April as larvae start very small and damage escalates rapidly',
      'Cultivate soil under plants in autumn to expose overwintering pupae',
    ],
  },

  {
    id: 'spotted-wing-drosophila',
    image: '/images/pests/spotted-wing-drosophila.png',
    name: 'Spotted Wing Drosophila',
    type: 'insect',
    tags: ['fruit-damage'],
    weatherTriggers: ['warm', 'humid'],
    description: 'An invasive fruit fly that unlike common vinegar flies lays eggs in intact ripening soft fruits, causing rapid internal rot and making fruit unmarketable.',
    symptoms: [
      'Soft sunken spots on intact ripening fruits',
      'Small white maggots inside the fruit flesh near the skin',
      'Rapid collapse and fermentation of infested berries',
    ],
    affectedPlants: ['Strawberry', 'Raspberry', 'Blueberry', 'Cherry', 'Blackberry', 'Plum', 'Grape'],
    organicSolutions: [
      {
        name: 'Fine mesh netting',
        description: 'Cover crops with fine insect-proof netting (less than 0.98 mm mesh) as fruit colours up to physically exclude egg-laying females.',
      },
      {
        name: 'Yeast-sugar traps',
        description: 'Hang red-balled traps with a yeast and sugar bait around crops to monitor adult presence and time protective measures.',
      },
      {
        name: 'Harvest promptly',
        description: 'Pick all ripe and over-ripe fruit every 1 - 2 days to remove the resource that attracts females, reducing local populations significantly.',
      },
    ],
    preventionTips: [
      'Destroy all unmarketable or over-ripe fruit by sealing in a plastic bag and leaving in the sun to kill any larvae inside',
      'Remove and destroy wild brambles near the garden which act as a reservoir host population',
    ],
  },

  {
    id: 'sunflower-moth',
    image: '/images/pests/sunflower-moth.png',
    name: 'Sunflower Moth',
    type: 'insect',
    tags: ['holes', 'chewing', 'fruit-damage'],
    weatherTriggers: ['warm', 'dry'],
    description: 'A small moth whose caterpillars feed in sunflower florets and developing seed heads, tunnelling through the developing seeds and leaving webbing and frass.',
    symptoms: [
      'Webbing and frass in the developing seed head',
      'Seeds hollowed out or destroyed in sectors of the head',
      'Caterpillars up to 20 mm found tunnelling inside florets',
    ],
    affectedPlants: ['Sunflower'],
    organicSolutions: [
      {
        name: 'Bacillus thuringiensis spray',
        description: 'Spray Bt onto flower heads at first bloom when young larvae are feeding on floret surfaces before burrowing into the seed head.',
      },
      {
        name: 'Hand removal',
        description: 'Inspect flower heads weekly and remove caterpillars by hand during early infestation.',
      },
    ],
    preventionTips: [
      'Plant sunflowers away from the previous year\'s sunflower location',
      'Remove crop debris promptly after harvest to eliminate overwintering pupae in seed head material',
    ],
  },

  {
    id: 'vine-weevil',
    image: '/images/pests/vine-weevil.png',
    name: 'Vine Weevil',
    type: 'insect',
    tags: ['wilt', 'yellowing', 'stunted', 'holes'],
    weatherTriggers: ['cool', 'wet-soil'],
    description: 'Dull black beetles 9 mm long whose cream larvae with brown heads devastate roots and crowns in pots and beds, causing sudden plant collapse in late summer.',
    symptoms: [
      'Adult notching: irregular U-shaped bites from leaf margins at night',
      'Plants wilting and collapsing suddenly, sliding out of pots with all roots gone',
      'Cream fat-bodied larvae with a brown head curled in the soil or compost',
    ],
    affectedPlants: ['Strawberry', 'Heuchera', 'Cyclamen', 'Begonia', 'Hosta', 'Primula', 'Grape', 'Raspberry'],
    organicSolutions: [
      {
        name: 'Beneficial nematodes',
        description: 'Water Steinernema kraussei nematodes into moist compost or soil in August and September when larvae are young and vulnerable - highly effective in pots.',
      },
      {
        name: 'Physical barriers',
        description: 'Fit copper tape around pot rims or place pots on copper mesh to deter adult weevils from climbing in to lay eggs.',
      },
      {
        name: 'Night checks',
        description: 'Check plants after dark with a torch and hand-pick adult beetles from leaf edges - adults cannot fly so populations are localised.',
      },
    ],
    preventionTips: [
      'Inspect bought-in plants and bare-root stock carefully for larvae in the root ball before planting',
      'Apply nematodes preventatively each August even if adults have not yet been seen',
    ],
  },

  {
    id: 'black-aphids',
    image: '/images/pests/black-aphids.png',
    name: 'Black Bean Aphid',
    type: 'insect',
    tags: ['sticky', 'yellowing', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'Dense colonies of shiny black aphids 2 mm long that mass on the tips of broad beans, spinach, and related plants, causing shoot distortion and sticky honeydew.',
    symptoms: [
      'Dense black colonies clustered on shoot tips and young leaves',
      'Shoot tips curling and distorting under the weight of colonies',
      'Sticky honeydew on leaves below the infestation, sometimes developing sooty mould',
    ],
    affectedPlants: ['Bean', 'Spinach', 'Beetroot', 'Chard', 'Nasturtium'],
    organicSolutions: [
      {
        name: 'Pinch out infested tips',
        description: 'Remove the top 5 cm of broad bean shoots as soon as colonies appear - this eliminates most of the population and stops further colonisation.',
      },
      {
        name: 'Insecticidal soap spray',
        description: 'Spray insecticidal soap solution directly onto colonies, covering all surfaces thoroughly; repeat every 5 - 7 days as needed.',
      },
      {
        name: 'Encourage ladybirds',
        description: 'Avoid pesticides to allow ladybirds, lacewings, and parasitic wasps to build up and consume colonies naturally.',
      },
    ],
    preventionTips: [
      'Pinch out broad bean tips as soon as pods begin to set to remove the tender growth aphids prefer',
      'Plant nasturtiums as a trap crop near bean rows to draw aphids away from the main crop',
    ],
  },

  {
    id: 'carrot-weevil',
    image: '/images/pests/carrot-weevil.png',
    name: 'Carrot Weevil',
    type: 'insect',
    tags: ['wilt', 'holes', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'A bronze-brown weevil 3 - 4 mm long whose larvae tunnel in a C-pattern through carrot taproots and crowns, causing plants to collapse and roots to rot.',
    symptoms: [
      'C-shaped tunnels in the upper portion of carrot roots when pulled',
      'Plants wilting suddenly in warm weather with the crown hollowed out',
      'Entry scars on the shoulder of roots where adults fed and laid eggs',
    ],
    affectedPlants: ['Carrot', 'Parsnip', 'Celery', 'Parsley'],
    organicSolutions: [
      {
        name: 'Beneficial nematodes',
        description: 'Apply Steinernema carpocapsae nematodes to moist soil at planting time and again in midsummer to control larvae in the root zone.',
      },
      {
        name: 'Crop rotation',
        description: 'Move all carrot family crops to a completely different bed each year as adults overwinter in field edges and nearby soil.',
      },
    ],
    preventionTips: [
      'Avoid planting carrots adjacent to last year\'s carrot bed',
      'Cover carrot rows with fine insect mesh to exclude egg-laying adults',
      'Cultivate soil deeply after harvest to expose overwintering adults and pupae',
    ],
  },

  {
    id: 'celery-leaf-miner',
    image: '/images/pests/celery-leaf-miner.png',
    name: 'Celery Leaf Miner',
    type: 'insect',
    tags: ['spots', 'holes', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'A small fly whose larvae create blotchy, discoloured mines inside celery and parsley leaves, turning leaves papery and reducing plant vigour.',
    symptoms: [
      'Large irregular pale blotch mines inside celery leaves when held to the light',
      'Leaves turning brown and papery where mining has consumed the tissue',
      'Stunted, bitter-tasting stems on heavily attacked plants',
    ],
    affectedPlants: ['Celery', 'Celeriac', 'Parsley'],
    organicSolutions: [
      {
        name: 'Row covers',
        description: 'Protect celery crops with fine insect mesh from transplanting through harvest to exclude egg-laying adults.',
      },
      {
        name: 'Remove mined leaves',
        description: 'Pick off and destroy blotched leaves as soon as they appear, before larvae complete development and pupate.',
      },
    ],
    preventionTips: [
      'Do not compost mined leaves as pupae may survive in the heap',
      'Rotate celery and parsley to a fresh bed each year',
    ],
  },

  {
    id: 'citrus-gall-wasp',
    image: '/images/pests/citrus-gall-wasp.png',
    name: 'Citrus Gall Wasp',
    type: 'insect',
    tags: ['stunted', 'wilt'],
    weatherTriggers: ['warm'],
    description: 'A small native Australian wasp whose larvae develop inside citrus stems, forming woody elongated galls that weaken branches and reduce fruit production.',
    symptoms: [
      'Elongated woody swellings on young branches, typically 5 - 20 cm long',
      'Reduced fruit production and dieback of branches beyond the gall',
      'Multiple galls coalescing on heavily infested young trees',
    ],
    affectedPlants: ['Lemon', 'Orange', 'Lime', 'Grapefruit', 'Citrus'],
    organicSolutions: [
      {
        name: 'Prune out galls',
        description: 'Cut branches containing galls 10 cm below the gall in August before adult emergence; seal cut ends with grafting wax or pruning paste.',
      },
      {
        name: 'Bag galls',
        description: 'Where pruning would remove too much structure, bag individual galls with tied plastic bags to trap emerging adults and prevent spread.',
      },
    ],
    preventionTips: [
      'Avoid heavy nitrogen feeding which promotes the soft new growth that females prefer for egg-laying',
      'Inspect trees annually in late summer for new galls while they are still small',
    ],
  },

  {
    id: 'comfrey-moth',
    image: '/images/pests/comfrey-moth.png',
    name: 'Comfrey Moth',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'The caterpillars of the scarlet tiger moth and related species that feed on comfrey foliage; feeding is rarely severe enough to harm established plants.',
    symptoms: [
      'Ragged holes in comfrey leaves, usually from the leaf edges inward',
      'Hairy caterpillars with black, red, and yellow markings found on leaves',
      'Partial defoliation of stems in heavy feeding seasons',
    ],
    affectedPlants: ['Comfrey'],
    organicSolutions: [
      {
        name: 'Hand removal',
        description: 'Pick caterpillars off by hand and relocate them to wild areas - the scarlet tiger moth is a beneficial species worth preserving.',
      },
    ],
    preventionTips: [
      'Established comfrey recovers quickly from caterpillar feeding - intervention is rarely necessary',
      'Allow some feeding to support moth and butterfly populations in the garden',
    ],
  },

  {
    id: 'fig-beetle',
    image: '/images/pests/fig-beetle.png',
    name: 'Fig Beetle',
    type: 'insect',
    tags: ['fruit-damage', 'holes'],
    weatherTriggers: ['warm', 'hot'],
    description: 'A large metallic green or copper-green scarab beetle up to 35 mm long that feeds on ripe figs and soft fruit, tearing open skin to access sweet flesh.',
    symptoms: [
      'Ripe figs torn open at the eye end with large irregular feeding cavities',
      'Large iridescent green beetles found feeding on or near damaged fruit',
      'Secondary fungal rot entering through feeding wounds on figs and other soft fruit',
    ],
    affectedPlants: ['Fig', 'Peach', 'Plum', 'Grape', 'Apple'],
    organicSolutions: [
      {
        name: 'Harvest promptly',
        description: 'Pick figs as soon as fully ripe to deny beetles access - overripe fruit hanging on the tree is the primary attractant.',
      },
      {
        name: 'Netting',
        description: 'Cover smaller trees with bird and insect netting during the ripening period to exclude beetles physically.',
      },
    ],
    preventionTips: [
      'Remove fallen and overripe fruit from the ground daily',
      'Reduce mulch depth near the tree trunk in spring to expose white grub larvae to birds and desiccation',
    ],
  },

  {
    id: 'parsley-worm',
    image: '/images/pests/parsley-worm.png',
    name: 'Parsley Worm',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'The caterpillar of the black swallowtail butterfly, bright green with black and yellow banding, found on carrot family plants; it becomes the beautiful swallowtail butterfly.',
    symptoms: [
      'Large, striking green caterpillars with black bands and yellow dots on leaves and stems',
      'Stems and leaves partially defoliated on carrot, parsley, or dill',
      'Orange Y-shaped osmeteria (scent organ) extended when the caterpillar is disturbed',
    ],
    affectedPlants: ['Carrot', 'Parsley', 'Dill', 'Fennel', 'Celery'],
    organicSolutions: [
      {
        name: 'Hand removal and relocation',
        description: 'Move caterpillars to a wild or dedicated patch of dill or parsley rather than killing them - they will become black swallowtail butterflies.',
      },
    ],
    preventionTips: [
      'Plant extra dill and parsley as a dedicated host plant patch to support butterfly populations away from main crops',
      'Damage to established carrot family plants is rarely severe enough to warrant control',
    ],
  },

  {
    id: 'pea-moth',
    image: '/images/pests/pea-moth.png',
    name: 'Pea Moth',
    type: 'insect',
    tags: ['fruit-damage', 'holes'],
    weatherTriggers: ['warm'],
    description: 'A small moth whose caterpillars burrow into pea pods and feed on the developing seeds, often going undetected until the pod is opened at harvest.',
    symptoms: [
      'Pea pods with small entry holes when examined closely',
      'Caterpillars and frass inside pods at harvest',
      'Some peas partially or wholly consumed within an otherwise normal-looking pod',
    ],
    affectedPlants: ['Pea'],
    organicSolutions: [
      {
        name: 'Timing',
        description: 'Sow peas early (February - March) or late (after June) so flowering occurs outside the peak moth flight period of mid-June through August.',
      },
      {
        name: 'Fine mesh cover',
        description: 'Cover pea rows with fine insect mesh during the flowering period to exclude egg-laying moths.',
      },
    ],
    preventionTips: [
      'Do not grow peas that will be flowering during July - this is when egg-laying peaks in most of the UK and northern Europe',
      'Remove crop debris promptly as pupae overwinter in the soil near where pods fell',
    ],
  },

  {
    id: 'pea-weevil',
    image: '/images/pests/pea-weevil.png',
    name: 'Pea and Bean Weevil',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['cool'],
    description: 'A small grey-brown weevil 4 - 5 mm long that grazes distinctive U-shaped notches from the leaf margins of peas and broad beans in spring.',
    symptoms: [
      'U-shaped or scalloped notches cut from leaf edges of pea or bean seedlings',
      'Small grey-brown weevils found on leaves when inspected closely',
      'Seedlings weakened or growth temporarily checked in severe infestations',
    ],
    affectedPlants: ['Pea', 'Bean'],
    organicSolutions: [
      {
        name: 'Encourage rapid establishment',
        description: 'Sow into warm, fertile, well-prepared soil so plants establish vigour quickly and grow away from weevil damage.',
      },
      {
        name: 'Row covers',
        description: 'Protect seedlings with fleece or insect mesh from emergence through to 10 cm tall when plants outgrow weevil vulnerability.',
      },
    ],
    preventionTips: [
      'Damage from adult feeding is rarely severe on established plants - the main risk is to very small seedlings',
      'Larvae feed on root nodules but rarely cause significant harm',
    ],
  },

  {
    id: 'pepper-weevil',
    image: '/images/pests/pepper-weevil.png',
    name: 'Pepper Weevil',
    type: 'insect',
    tags: ['fruit-damage', 'wilt'],
    weatherTriggers: ['warm', 'hot'],
    description: 'A small dark weevil 2 - 3 mm long whose larvae develop inside pepper fruits, causing them to turn yellow and drop prematurely well before maturity.',
    symptoms: [
      'Peppers turning yellow and dropping from the plant while still small',
      'Larvae and frass found inside dropped immature fruit',
      'Adult feeding punctures visible as tiny holes at the fruit surface',
    ],
    affectedPlants: ['Pepper'],
    organicSolutions: [
      {
        name: 'Remove dropped fruit',
        description: 'Collect and destroy all dropped peppers immediately to prevent larvae completing development and pupating in the soil.',
      },
      {
        name: 'Row covers',
        description: 'Cover pepper plants with fine insect mesh to exclude adults, removing for ventilation during the hottest part of the day.',
      },
    ],
    preventionTips: [
      'Inspect plants daily during fruiting and remove any peppers showing yellowing before larvae can complete development',
      'Avoid growing peppers in the same spot for consecutive seasons',
    ],
  },

  {
    id: 'pomegranate-butterfly',
    image: '/images/pests/pomegranate-butterfly.png',
    name: 'Pomegranate Butterfly',
    type: 'insect',
    tags: ['fruit-damage', 'holes'],
    weatherTriggers: ['warm'],
    description: 'A striking blue and black butterfly whose caterpillars bore into pomegranate fruits and flower buds, causing premature fruit drop and reducing yield significantly.',
    symptoms: [
      'Entry holes in developing fruits covered with frass',
      'Fruits dropping prematurely with larvae found inside on dissection',
      'Flower buds hollowed out before they can open',
    ],
    affectedPlants: ['Pomegranate'],
    organicSolutions: [
      {
        name: 'Bag fruits',
        description: 'Slip paper or muslin bags over individual fruits after fruit set to physically exclude egg-laying females.',
      },
      {
        name: 'Remove infested fruit',
        description: 'Collect and destroy all infested fruits immediately to prevent larvae completing development and pupating.',
      },
    ],
    preventionTips: [
      'Inspect fruits weekly from the time they are thumbnail-sized',
      'Keep the area under trees clean of fallen fruit to reduce pupation sites',
    ],
  },

  {
    id: 'rootworm',
    image: '/images/pests/rootworm.png',
    name: 'Corn Rootworm',
    type: 'insect',
    tags: ['wilt', 'stunted', 'yellowing'],
    weatherTriggers: ['warm'],
    description: 'Slender white beetle larvae up to 12 mm long that feed on corn roots, pruning them to stubs and causing plants to lodge and fall over in wind.',
    symptoms: [
      'Corn plants leaning or lodging at an angle due to root damage',
      'Roots pruned back to stubs at the crown when plants are pulled',
      'Silk clipping by adult beetles reducing pollination during tasselling',
    ],
    affectedPlants: ['Corn'],
    organicSolutions: [
      {
        name: 'Crop rotation',
        description: 'Never plant corn in the same field or bed two years running - eggs are laid in corn soil and larvae hatch expecting corn roots the following spring.',
      },
      {
        name: 'Beneficial nematodes',
        description: 'Apply Heterorhabditis bacteriophora nematodes to corn fields at egg hatch in early summer to target larvae before root pruning begins.',
      },
    ],
    preventionTips: [
      'Strict crop rotation is the single most effective management tool - one year\'s break prevents near-complete root damage',
      'Hill up soil around corn bases at the knee-high stage to encourage brace roots that compensate for root damage',
    ],
  },

  {
    id: 'rosemary-beetle',
    image: '/images/pests/rosemary-beetle.png',
    name: 'Rosemary Beetle',
    type: 'insect',
    tags: ['holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'A beautiful metallic green and purple striped beetle 6 - 8 mm long, along with its grey striped larvae, that strips rosemary, lavender, and thyme foliage.',
    symptoms: [
      'Rosemary and lavender shoots partially defoliated, leaving bare twiggy stems',
      'Metallic green-purple adult beetles and striped grey larvae visible on stems',
      'Feeding concentrated on young shoot tips and flower buds',
    ],
    affectedPlants: ['Rosemary', 'Lavender', 'Thyme', 'Sage'],
    organicSolutions: [
      {
        name: 'Hand-picking',
        description: 'Shake plants gently over a light-coloured sheet and collect all beetles and larvae that fall; repeat every 1 - 2 weeks through the season.',
      },
      {
        name: 'Winter pruning',
        description: 'Prune plants hard after flowering to remove overwintering adults hiding in woody growth at the plant centre.',
      },
    ],
    preventionTips: [
      'Check plants regularly from September through spring when adults are most active',
      'Clear mulch and debris from around plant bases where adults shelter',
    ],
  },

  {
    id: 'scale-insects',
    image: '/images/pests/scale-insects.png',
    name: 'Scale Insects',
    type: 'insect',
    tags: ['sticky', 'yellowing', 'stunted'],
    weatherTriggers: ['warm'],
    description: 'Flat or domed immobile insects that attach to stems and leaves beneath a protective waxy shell, sucking sap and excreting honeydew that promotes sooty mould.',
    symptoms: [
      'Flat or domed brown, cream, or white waxy lumps on stems and leaf undersides',
      'Sticky honeydew on leaves below infested stems',
      'Sooty black mould growing on honeydew deposits',
      'Yellowing foliage and reduced vigour on heavily infested plants',
    ],
    affectedPlants: ['Apple', 'Citrus', 'Lemon', 'Orange', 'Fig', 'Grape', 'Peach', 'Plum', 'Rose', 'Bay'],
    organicSolutions: [
      {
        name: 'Horticultural oil spray',
        description: 'Apply winter dormant oil spray thoroughly to all bark surfaces to smother overwintering scales before eggs hatch in spring.',
      },
      {
        name: 'Manual removal',
        description: 'Scrub scale colonies off stems with a soft brush dipped in soapy water or methylated spirits during the growing season.',
      },
      {
        name: 'Encourage natural enemies',
        description: 'Avoid pesticides as parasitic wasps and ladybird larvae are highly effective natural predators when populations are left undisturbed.',
      },
    ],
    preventionTips: [
      'Inspect bought-in plants carefully before introducing them to the garden',
      'Apply dormant oil every winter to prevent population build-up on fruit trees',
    ],
  },

  {
    id: 'sweet-potato-weevil',
    image: '/images/pests/sweet-potato-weevil.png',
    name: 'Sweet Potato Weevil',
    type: 'insect',
    tags: ['wilt', 'holes', 'stunted'],
    weatherTriggers: ['warm', 'hot'],
    description: 'A slender weevil with a blue-black body and orange-red legs whose larvae tunnel through sweet potato roots and stems, making tubers inedible.',
    symptoms: [
      'Tunnels and frass throughout the tuber flesh when harvested',
      'Characteristic bitter, turpentine-like taste in infested tubers',
      'Adults found on soil surface near plant crowns when disturbed',
    ],
    affectedPlants: ['Sweet Potato'],
    organicSolutions: [
      {
        name: 'Use clean planting material',
        description: 'Start only from certified weevil-free slips - the weevil is primarily moved from garden to garden on infested planting material.',
      },
      {
        name: 'Harvest on time',
        description: 'Harvest tubers as soon as mature and before the soil cracks, as cracks provide adult weevils with direct access to the tubers.',
      },
    ],
    preventionTips: [
      'Hill up soil over the tubers throughout the growing season to prevent cracks forming',
      'Avoid replanting sweet potato in the same bed the following season',
    ],
  },

  {
    id: 'vine-borer',
    image: '/images/pests/vine-borer.png',
    name: 'Vine Borer',
    type: 'insect',
    tags: ['wilt', 'holes', 'chewing'],
    weatherTriggers: ['warm'],
    description: 'A clearwing moth whose larvae bore into the base of grape vines and other woody plants, girdling stems and causing sudden wilting or complete cane death.',
    symptoms: [
      'Entry holes at or just below the soil line on vine canes with sawdust frass',
      'Sudden wilting and collapse of individual canes during the growing season',
      'Larvae found inside hollowed stems when canes are cut open',
    ],
    affectedPlants: ['Grape', 'Raspberry', 'Currant'],
    organicSolutions: [
      {
        name: 'Manual removal',
        description: 'Probe entry holes with a thin wire to kill larvae, or slit the stem and remove them by hand, then bind the stem back together.',
      },
      {
        name: 'Beneficial nematodes',
        description: 'Inject Steinernema carpocapsae nematodes in water into entry holes to target larvae inside the stem.',
      },
    ],
    preventionTips: [
      'Wrap the lower 30 cm of canes in fine mesh or foil during peak adult flight in early summer',
      'Check cane bases carefully each spring for entry holes before the growing season starts',
    ],
  },

  {
    id: 'wasps',
    image: '/images/pests/wasps.png',
    name: 'Wasps',
    type: 'insect',
    tags: ['fruit-damage', 'holes'],
    weatherTriggers: ['warm', 'hot'],
    description: 'Social wasps that in late summer shift from eating insects to feeding on sugar, boring into ripe soft fruit and grapes and enlarging existing damage rapidly.',
    symptoms: [
      'Ripe fruits with large ragged cavities where wasps have hollowed out the flesh',
      'Damage concentrated in late summer and early autumn when wasp colonies are largest',
      'Secondary fungal rot developing in feeding wounds',
    ],
    affectedPlants: ['Apple', 'Pear', 'Plum', 'Grape', 'Fig', 'Peach', 'Strawberry'],
    organicSolutions: [
      {
        name: 'Fine mesh bags',
        description: 'Slip individual paper or muslin fruit bags over clusters of grapes or individual fruit to physically exclude wasps from ripening fruit.',
      },
      {
        name: 'Wasp traps',
        description: 'Hang wasp traps baited with sugary liquid around the garden from late July to reduce local populations before peak damage season.',
      },
    ],
    preventionTips: [
      'Harvest fruit as soon as ripe - do not leave over-ripe fruit on the tree',
      'Remove fallen fruit daily during late summer as it rapidly attracts large numbers',
      'Locate nest sites in spring when colonies are small and have them treated if close to the garden',
    ],
  },

  // ── DISEASES ───────────────────────────────────────────────────────────────

  {
    id: 'powdery-mildew',
    image: '/images/pests/powdery-mildew.png',
    name: 'Powdery Mildew',
    type: 'disease',
    tags: ['powder', 'yellowing'],
    weatherTriggers: ['humid', 'warm', 'dry'],
    description: 'One of the most common fungal diseases in the garden, caused by several related species. Unlike most fungi, powdery mildew actually thrives in warm, dry weather with poor air circulation, rather than in wet conditions. It affects an enormous range of crops and is almost universal in humid temperate climates.',
    symptoms: [
      'White or grey powdery coating on upper leaf surfaces, appearing as if the leaves were dusted with flour',
      'Distorted, curling, or stunted new shoots',
      'Yellowing of leaves beneath the white coating',
      'Premature leaf drop and dieback of affected shoots',
      'The white coating can initially be rubbed off, but as the disease progresses it becomes permanent',
    ],
    affectedPlants: ['Cucumber', 'Zucchini', 'Butternut Squash', 'Watermelon', 'Cantaloupe', 'Peas', 'Kale', 'Tomato', 'Eggplant', 'Bee Balm', 'Calendula', 'Zinnia', 'Cosmos'],
    organicSolutions: [
      { name: 'Baking Soda Spray', description: 'Mix 1 tablespoon of baking soda, 1 tablespoon of neem oil, and a few drops of castile soap per litre of water. The alkaline coating inhibits spore germination on the leaf surface. Apply to all leaf surfaces every 7 - 10 days, or after rain. Most effective as a preventive.' },
      { name: 'Milk Spray', description: 'A 40% solution of fresh milk in water is as effective as many chemical fungicides in several published studies. Proteins in milk react with sunlight to produce antiseptic compounds on the leaf surface. Spray upper leaf surfaces weekly in warm, dry weather.' },
      { name: 'Neem Oil', description: 'Neem oil has proven antifungal properties and disrupts the spore germination cycle. Apply as a preventive spray at the first sign of conditions favouring disease (warm days, cool nights) and as a treatment at the earliest symptom stage.' },
      { name: 'Improve Airflow', description: 'Prune affected foliage, remove dense interior growth, and increase spacing between plants. Powdery mildew spores germinate on leaf surfaces but struggle to establish where air moves freely. Ventilation is the most durable preventive measure.' },
    ],
    preventionTips: [
      'Choose disease-resistant varieties of cucumber, courgette, and squash - many modern varieties have excellent powdery mildew resistance built in',
      'Avoid overhead watering, which wets foliage that remains damp and promotes all fungal diseases',
      'Maintain adequate spacing between plants from the time of transplanting: crowding is the primary cause of powdery mildew outbreaks in vegetable gardens',
      'Spray aerated compost tea as a foliar application every two weeks — the beneficial microorganisms colonise leaf surfaces and actively compete with powdery mildew spores, significantly reducing disease pressure on plants growing in biologically active soil',
    ],
  },

  {
    id: 'early-blight',
    image: '/images/pests/early-blight.png',
    name: 'Early Blight',
    type: 'disease',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['rain', 'humid', 'warm'],
    description: 'A fungal disease caused by Alternaria solani that affects tomatoes and related crops. It begins on the oldest lower leaves and progressively moves up the plant over the season. It rarely kills established plants outright but reduces vigour, causes premature defoliation, and can significantly reduce yield.',
    symptoms: [
      'Dark brown or black target-shaped spots on lower, older leaves - the bullseye pattern is diagnostic',
      'Concentric rings within each spot, resembling a dartboard or tree rings',
      'Yellow halo of chlorotic tissue surrounding the dark central spot',
      'Affected leaves yellowing fully and dropping prematurely',
      'Lesions first appearing on the lowest leaves and steadily advancing upward over weeks',
    ],
    affectedPlants: ['Tomato', 'Bell Pepper', 'Eggplant', 'Jalapeño Pepper', 'Celery'],
    organicSolutions: [
      { name: 'Remove Affected Leaves Promptly', description: 'At the first sign of target spots, remove all affected lower leaves and dispose of them away from the garden in household waste. Never compost blighted material. This single action significantly slows the upward spread of the disease.' },
      { name: 'Copper Fungicide Spray', description: 'Copper-based sprays (copper hydroxide or copper octanoate, approved for organic use) applied at the first sign of infection protect remaining healthy tissue. Apply every 7–14 days. Use sparingly — copper accumulates in soil over time and can harm earthworms and soil biology at high concentrations. Reserve for significant disease pressure.' },
      { name: 'Neem Oil', description: 'Apply neem oil spray to the entire plant every 7 - 14 days from transplant time as a preventive. The antifungal compounds in neem suppress Alternaria spore germination and early colonisation.' },
      { name: 'Compost Tea', description: 'Regular applications of aerated compost tea inoculate leaf surfaces with beneficial microorganisms that compete with pathogenic fungi. Apply as a foliar spray in the morning every two weeks as a preventive.' },
    ],
    preventionTips: [
      'Mulch heavily around the base of all nightshade plants - most early blight inoculum lives in the soil and is splash-distributed onto lower leaves by rain and irrigation',
      'Rotate tomato family crops with at least 3 years between plantings in the same bed - Alternaria spores survive in crop debris in the soil',
      'Water at the base of plants only, and avoid wetting foliage, particularly in the evening when moisture persists overnight',
      'Apply aerated compost tea as a foliar spray every two weeks from transplanting — plants growing in biologically active soil with regular compost tea applications show significantly better resistance to Alternaria. Botanical brew made from comfrey and nettle strengthens cell walls, making them more resistant to fungal penetration',
    ],
  },

  {
    id: 'late-blight',
    image: '/images/pests/late-blight.png',
    name: 'Late Blight',
    type: 'disease',
    tags: ['spots', 'wilt'],
    weatherTriggers: ['rain', 'humid', 'cool'],
    description: 'The most destructive disease of tomatoes and potatoes worldwide, caused by the water mould Phytophthora infestans - the same organism responsible for the Irish Potato Famine. In cool, wet weather it can kill an entire plant within days. Once it is established in a planting, complete loss is possible.',
    symptoms: [
      'Large, irregular, dark greasy-looking patches on leaves, often with a pale greenish halo',
      'White, downy sporulation on the underside of affected patches during humid nights',
      'Dark brown to black lesions on stems that girdle and cause stem collapse',
      'Firm, dark brown patches spreading across fruit',
      'Entire plants collapsing very rapidly under cool, wet conditions',
    ],
    affectedPlants: ['Tomato', 'Bell Pepper', 'Eggplant', 'Jalapeño Pepper'],
    organicSolutions: [
      { name: 'Remove and Destroy All Infected Material Immediately', description: 'Late blight spreads by airborne spores at extraordinary speed. At the first sign of symptoms, remove all infected foliage and fruit, bag them, and place in household waste. Never compost. Wash hands and disinfect tools between plants with a bleach solution.' },
      { name: 'Preventive Copper Spray', description: 'Copper sprays must be applied before infection to be effective — they protect leaf surfaces against spore germination but cannot cure established infections. Apply weekly during high-risk periods (cool temperatures 10–20°C, wet or foggy conditions). Use sparingly — copper accumulates in soil over time and can harm earthworms and soil biology at high concentrations.' },
      { name: 'Improve Airflow and Staking', description: 'Ensure all tomato plants are staked and vertical, with no leaves touching the soil, and that plants have adequate spacing. Late blight requires sustained leaf wetness to germinate - reducing this time is critical to slowing spread.' },
    ],
    preventionTips: [
      'Choose blight-tolerant tomato varieties where late blight is a known annual problem in your area',
      'Never leave tomato or pepper debris in the soil over winter - Phytophthora oospores survive in crop residues',
      'Avoid overhead watering entirely and time irrigation for early morning so foliage dries quickly',
      'Build soil health consistently — Phytophthora is suppressed in soils with high biological diversity. Large annual additions of compost and regular compost tea applications create the microbial environment that naturally antagonises this pathogen',
    ],
  },

  {
    id: 'damping-off',
    image: '/images/pests/damping-off.png',
    name: 'Damping Off',
    type: 'disease',
    tags: ['wilt', 'stunted'],
    weatherTriggers: ['rain', 'humid', 'cool', 'wet-soil'],
    description: 'A collective term for seedling collapse caused by soil fungi and water moulds (Pythium, Fusarium, Rhizoctonia) that attack seeds and seedlings at or below the soil surface. It is the most common cause of seedling death in indoor propagation and is caused almost entirely by overwatering and poor airflow - both of which are fully preventable.',
    symptoms: [
      'Seedlings collapsing suddenly and lying flat, appearing pinched or constricted at the soil surface',
      'The collapsed stem is water-soaked, brown, or shrunken at the point of failure - the stem below is fine',
      'Previously healthy seedlings falling over overnight with no recovery',
      'White or grey mould on the soil surface around affected seedlings',
      'Poor germination rates in trays that were kept too wet',
    ],
    affectedPlants: ['Tomato', 'Bell Pepper', 'Eggplant', 'Cucumber', 'Basil', 'Lettuce', 'Kale', 'Broccoli', 'Zinnia', 'Cosmos', 'Sunflower'],
    organicSolutions: [
      { name: 'Improve Air Circulation', description: 'Introduce a small fan to your propagation space, set on the lowest setting. Even intermittent air movement dramatically reduces the surface humidity that damping-off pathogens require. 30 minutes of air movement per day reduces incidence significantly.' },
      { name: 'Water from Below', description: 'Place seed trays in a shallow tray of water and allow compost to absorb moisture from below. This keeps the soil surface drier and deprives pathogens of the surface moisture they need to spread. Allow the surface to partially dry between waterings.' },
      { name: 'Use Fresh, Sterile Seed Compost', description: 'Always use fresh, purpose-made seed-starting compost rather than reused potting mix or garden soil. Damping-off pathogens are abundant in non-sterile growing media. Start every new batch of seedlings in a fresh, sterile medium.' },
      { name: 'Chamomile Tea Drench', description: 'Brew a strong chamomile tea, cool it, and use it to water seedlings once a week. Chamomile contains trans-farnesol and other compounds with documented antifungal activity against Pythium and Botrytis species.' },
    ],
    preventionTips: [
      'Never overwater seedlings - this is the single most impactful preventive measure for damping off',
      'Sow seeds thinly so seedlings are not crowded; dense planting traps moisture and reduces essential airflow around stems',
      'Sterilise reused trays and pots in a 10% bleach solution, rinse well, and dry before use - pathogens persist on plastic surfaces between sowings',
      'Add a small amount of worm castings to your seed-starting mix — the beneficial bacteria naturally present in castings actively suppress damping-off pathogens and give seedlings a biological advantage from germination',
    ],
  },

  {
    id: 'downy-mildew',
    image: '/images/pests/downy-mildew.png',
    name: 'Downy Mildew',
    type: 'disease',
    tags: ['powder', 'yellowing', 'spots'],
    weatherTriggers: ['rain', 'humid', 'cool'],
    description: 'A water mould (oomycete) disease that, despite its name, is not closely related to powdery mildew. It thrives in the opposite conditions - cool, wet, and humid weather - and different strains are specific to different plant families. It can be confused with powdery mildew but the spore growth is on the underside of leaves, not the top.',
    symptoms: [
      'Angular yellow or pale green patches on the upper leaf surface, bounded by leaf veins',
      'Grey-white, purple, or lavender fuzzy sporulation on the underside of the yellow patches',
      'Patches enlarging and coalescing, eventually turning brown as affected tissue dies',
      'Leaves wilting, browning, and dropping in severe cases',
      'Fluffy white fuzz visible on leaf undersides on humid mornings',
    ],
    affectedPlants: ['Spinach', 'Lettuce', 'Cucumber', 'Peas', 'Onion', 'Beet', 'Swiss Chard', 'Basil', 'Kale', 'Broccoli', 'Grape'],
    organicSolutions: [
      { name: 'Remove Infected Leaves', description: 'Remove all visibly affected foliage immediately. Bag it and dispose of in household waste - do not compost. Prompt removal reduces the volume of spores being produced and spread.' },
      { name: 'Copper-Based Spray', description: 'Copper hydroxide or copper octanoate sprays applied preventively, or at the very first sign of symptoms, provide significant protection. Apply in the early morning to allow foliage to dry before evening. Use sparingly — copper accumulates in soil over time and can harm earthworms and soil biology at high concentrations.' },
      { name: 'Improve Ventilation', description: 'Increase plant spacing and avoid wetting foliage. Downy mildew requires extended leaf wetness to germinate. Improve airflow around susceptible crops like basil and spinach through regular thinning and generous spacing.' },
      { name: 'Potassium Bicarbonate Spray', description: 'A spray of potassium bicarbonate (1 tablespoon per litre of water with a drop of soap) raises the surface pH of leaves, inhibiting spore germination. More effective as a preventive than as a cure. Apply every 7 - 10 days in humid weather.' },
    ],
    preventionTips: [
      'Water only in the morning so foliage is completely dry by nightfall - downy mildew spores need several hours of leaf wetness to germinate',
      'Choose downy mildew resistant varieties where available, particularly for basil (which is extremely susceptible to basil downy mildew) and spinach',
      'Rotate susceptible crops to a different bed each year and avoid planting the same family in wet, shaded positions',
      'Apply compost tea as a foliar spray every two weeks during humid conditions — the beneficial microorganisms in well-made compost tea compete directly with downy mildew spores and help prevent them establishing on leaf surfaces',
    ],
  },

  {
    id: 'fire-blight',
    image: '/images/pests/fire-blight.png',
    name: 'Fire Blight',
    type: 'disease',
    tags: ['wilt', 'spots'],
    weatherTriggers: ['warm', 'humid', 'rain'],
    description: 'Erwinia amylovora is a highly destructive bacterial disease that spreads rapidly in warm, wet conditions during flowering. The bacteria enter through flowers, young shoots, and wounds, and move through the vascular tissue. Infected shoots take on a characteristic burnt, scorched appearance and bend into a distinctive "shepherd\'s crook" at their tips. A severe outbreak can destroy a significant portion of a tree\'s canopy in a single season.',
    symptoms: [
      'Shoot tips wilting and bending into a tight shepherd\'s crook shape, as if scorched',
      'Leaves on infected shoots turning brown or black and remaining attached to the branch rather than falling',
      'Brown, water-soaked, or sunken lesions extending down branches from an infected shoot tip',
      'White or amber bacterial ooze appearing on bark lesions in warm, humid conditions',
      'Cankers on older wood with a reddish-brown discolouration visible when bark is cut away',
    ],
    affectedPlants: ['Apple', 'Pear', 'Quince', 'Hawthorn', 'Mountain Ash', 'Serviceberry', 'Crabapple'],
    organicSolutions: [
      { name: 'Prune Out Infected Wood Immediately', description: 'Cut infected shoots at least 30 cm below any visible discolouration, cutting into healthy wood with a clear white interior. Disinfect cutting tools with 70% alcohol or a 10% bleach solution between every cut. Remove all prunings from the site and burn or bag them - do not compost infected material.' },
      { name: 'Copper-Based Spray at Dormancy', description: 'Apply a copper hydroxide or copper sulphate spray during full dormancy in late winter, before any bud movement. Copper at dormancy kills overwintering bacterial cankers on the bark surface and reduces the amount of inoculum available for the following spring. Follow label rates carefully.' },
      { name: 'Copper Spray at Bloom', description: 'Apply dilute copper sprays every 3 - 5 days during bloom in warm, humid, or rainy weather when infection risk is highest. Cover all flower clusters thoroughly. Limit bloom-period copper to the minimum rate that is effective to avoid fruit russet, which copper can cause at higher rates.' },
      { name: 'Avoid Stimulating Soft Growth', description: 'Fire blight is far more severe on trees with an abundance of soft, rapidly growing shoots. Avoid high-nitrogen fertilisers and heavy pruning in spring, both of which stimulate the susceptible succulent growth that is preferentially infected. A steady, moderate growth rate produces more resistant tissue.' },
    ],
    preventionTips: [
      'Plant fire blight-resistant varieties wherever possible - Enterprise, Goldrush, Liberty, and Pristine in apples, and Moonglow, Harrow Sweet, and Harrow Delight in pears have strong genetic resistance',
      'Prune out all known cankers in late winter during dry weather before the disease becomes active - do not leave any infected wood on the tree through the growing season',
      'Apply a dilute copper spray every 3 - 5 days during bloom in warm, rainy springs - the flower is the primary infection site and the most critical period to cover',
      'Avoid sprinkler or overhead irrigation that wets foliage and flowers during bloom and summer growth periods - fire blight bacteria spread rapidly in surface moisture films',
      'Disinfect all cutting tools with alcohol between every cut during the growing season - cutting tools carry bacteria between trees and between cuts on the same tree',
    ],
  },

  {
    id: 'bacterial-wilt',
    image: '/images/pests/bacterial-wilt.png',
    name: 'Bacterial Wilt',
    type: 'disease',
    tags: ['wilt'],
    description: 'A bacterial disease caused by Erwinia tracheiphila, transmitted exclusively by cucumber beetles as they feed. The bacteria colonise the plant\'s water-conducting xylem vessels, blocking water movement and causing rapid collapse. There is no treatment once a plant is infected - prevention and cucumber beetle management are the only defences.',
    symptoms: [
      'Individual vines wilting suddenly and not recovering even after adequate watering',
      'The entire plant collapsing over one to three days following initial vine wilt',
      'Diagnostic test: cut a wilting stem and slowly pull the cut ends apart - sticky white threads bridging the cut indicate bacterial wilt',
      'No recovery even in cool overnight temperatures',
      'Death of the entire plant within 7 - 14 days of initial symptoms',
    ],
    affectedPlants: ['Cucumber', 'Zucchini', 'Butternut Squash', 'Watermelon', 'Cantaloupe'],
    organicSolutions: [
      { name: 'Remove Infected Plants Immediately', description: 'There is no cure for bacterial wilt. Remove wilting plants promptly to prevent cucumber beetles visiting them and carrying bacteria to healthy plants. Place in household waste and do not compost.' },
      { name: 'Control Cucumber Beetles', description: 'Because cucumber beetles are the sole vector, preventing beetle feeding is the only way to prevent bacterial wilt. Use row covers from planting, kaolin clay, yellow sticky traps, and companion planting to manage beetle populations before they transmit the disease.' },
      { name: 'Row Covers on Seedlings', description: 'Cover cucurbit transplants with fine mesh from the day of planting until plants are large and established. Even two to three weeks of beetle exclusion during the vulnerable seedling stage substantially reduces wilt incidence.' },
    ],
    preventionTips: [
      'Choose wilt-tolerant cucumber varieties in areas with historically high cucumber beetle pressure',
      'Delay planting cucurbits by 2 - 3 weeks where possible to miss the peak emergence of overwintered beetles, which are most numerous and hungry in late spring',
      'Manage cucumber beetle populations throughout the season with integrated methods - even a few beetles are tolerable; large populations rapidly become a disease vector problem',
    ],
  },

  {
    id: 'fusarium-wilt',
    image: '/images/pests/fusarium-wilt.png',
    name: 'Fusarium Wilt',
    type: 'disease',
    tags: ['wilt', 'yellowing'],
    description: 'A soil-borne fungal disease caused by Fusarium oxysporum with many crop-specific strains. The fungus enters through the roots and colonises the vascular system, blocking water transport. It can persist viable in soil for 20 years or more, making rotation planning critical for affected beds.',
    symptoms: [
      'One-sided yellowing and wilting - often only one branch or one half of the plant wilts first',
      'Brown discolouration in stem tissue visible when the stem is cut lengthwise, particularly near the base',
      'Stunted growth and progressive wilting despite adequate watering',
      'Lower leaves yellowing before upper ones, then entire plant decline',
      'Pink or white fungal growth at the stem base in warm, humid conditions',
    ],
    affectedPlants: ['Tomato', 'Watermelon', 'Cucumber', 'Basil', 'Asparagus', 'Strawberry', 'Peas', 'Celery'],
    organicSolutions: [
      { name: 'Remove Infected Plants', description: 'Remove affected plants with as much root and surrounding soil as possible. Do not compost - the fungus persists in infected roots for many years. Dispose of in household waste.' },
      { name: 'Soil Solarisation', description: 'Cover the infected bed with clear plastic sheeting for 4 - 6 weeks in summer. The trapped solar heat raises soil temperatures to levels that significantly reduce Fusarium populations in the top 15 cm. Most effective in hot, sunny climates.' },
      { name: 'Trichoderma Inoculant', description: 'Trichoderma is a beneficial soil fungus naturally antagonistic to Fusarium. Apply a commercial Trichoderma inoculant to the root zone when transplanting susceptible crops to help suppress Fusarium populations and protect new root growth.' },
      { name: 'Raise Soil pH', description: 'Fusarium prefers slightly acidic soils. Adding lime, wood ash, and ensuring good calcium levels through compost creates conditions less favourable to the pathogen. Aim for a soil pH of 6.5 - 7.0 in affected beds.' },
    ],
    preventionTips: [
      'Grow Fusarium-resistant tomato varieties - look for \'F\' designation in variety names, indicating tested resistance to Fusarium oxysporum race 1',
      'Practice strict crop rotation with at least 4 years between the same plant family in any bed',
      'Avoid overwatering and ensure excellent drainage - saturated soils favour Fusarium spore germination and root infection',
      'Build soil biological diversity through consistent compost additions and compost tea applications — Trichoderma and other Fusarium-antagonistic organisms thrive in soils rich in organic matter and microbial diversity. A living soil is your most durable Fusarium suppression strategy',
    ],
  },

  {
    id: 'root-rot',
    image: '/images/pests/root-rot.png',
    name: 'Root Rot',
    type: 'disease',
    tags: ['wilt', 'yellowing', 'stunted'],
    description: 'A collective term for root disease caused by water moulds and fungi (primarily Pythium and Phytophthora) that attack roots in waterlogged or poorly-drained soils. It is almost entirely a condition-driven problem - the pathogens are ubiquitous in soil, but only cause disease when plants are kept too wet. Improving drainage resolves most cases.',
    symptoms: [
      'Plants wilting despite moist or wet soil',
      'Yellowing, pale, or stunted foliage on plants that are otherwise being well cared for',
      'Brown, mushy, foul-smelling roots when the plant is removed from the soil',
      'Gradual decline over days to weeks with no recovery',
      'Roots that are dark, slimy, and disintegrate when handled (healthy roots are white and firm)',
    ],
    affectedPlants: ['Tomato', 'Bell Pepper', 'Cucumber', 'Zucchini', 'Basil', 'Rosemary', 'Lavender', 'Strawberry', 'Blueberry', 'Carrot', 'Onion', 'Garlic'],
    organicSolutions: [
      { name: 'Improve Drainage', description: 'The primary intervention is to eliminate the cause: improve soil drainage by incorporating organic matter, building raised beds, or establishing drainage channels. No treatment is effective while roots remain in waterlogged conditions.' },
      { name: 'Hydrogen Peroxide Drench', description: 'A solution of 1 part 3% hydrogen peroxide to 4 parts water applied as a soil drench oxygenates the root zone and can kill Pythium and Phytophthora on contact. Repeat twice weekly during recovery to support surviving root tissue.' },
      { name: 'Beneficial Microbe Inoculants', description: 'Trichoderma fungi, mycorrhizal inoculants, and beneficial bacteria (Bacillus subtilis) applied to the root zone suppress root pathogens and support root recovery. Use as a drench when replanting in previously affected areas.' },
    ],
    preventionTips: [
      'Never water until the top 2–3 cm of soil is dry to the touch - most root rot in gardens is caused by chronic overwatering, not by external infection',
      'Build raised beds with free-draining compost-rich soil in any area with heavy clay or water-retaining ground',
      'Incorporate perlite, coarse grit, or sharp sand when growing moisture-sensitive plants such as rosemary, lavender, and garlic',
      'Inoculate planting holes with mycorrhizal fungi when transplanting — mycorrhizal networks help roots access water and nutrients efficiently and strengthen root resistance to Pythium and Phytophthora. Use alongside generous compost additions for best results',
    ],
  },

  {
    id: 'mosaic-virus',
    image: '/images/pests/mosaic-virus.png',
    name: 'Mosaic Virus',
    type: 'disease',
    tags: ['yellowing', 'stunted'],
    description: 'A family of plant viruses transmitted primarily by aphids that cannot be cured once a plant is infected. Multiple species infect different hosts - Cucumber Mosaic Virus, Tomato Mosaic Virus, and Tobacco Mosaic Virus are among the most common. Management focuses entirely on prevention and immediate removal of infected plants.',
    symptoms: [
      'Mottled yellow and dark green patterning on leaves - the mosaic or marbling effect that gives the disease its name',
      'Leaves distorted, crinkled, puckered, or with an unusual bumpy texture',
      'Stunted, bushy growth with shortened internodes and reduced vigour',
      'Pale, mottled, or poorly-coloured fruit with poor flavour and reduced yield',
      'Overall plant stunting disproportionate to growing conditions',
    ],
    affectedPlants: ['Tomato', 'Cucumber', 'Zucchini', 'Bell Pepper', 'Spinach', 'Lettuce', 'Beet', 'Celery', 'Peas', 'Green Beans'],
    organicSolutions: [
      { name: 'Remove Infected Plants Immediately', description: 'There is no cure for mosaic viruses. Remove and bag infected plants immediately and place in household waste. Do not handle healthy plants after touching infected ones without washing hands with soap.' },
      { name: 'Rigorous Aphid Control', description: 'Since aphids are the primary vector, managing aphid populations is the most effective way to prevent mosaic virus spread. Encourage natural predators, use neem oil and insecticidal soap, and remove aphid colonies immediately when detected.' },
      { name: 'Disinfect Tools and Wash Hands', description: 'Tobacco Mosaic Virus can spread mechanically on hands and tools. Wash hands with soap between plants when pruning or training. Dip pruning tools in a 10% bleach solution between plants. Avoid smoking near tomatoes.' },
    ],
    preventionTips: [
      'Use certified virus-free seed and purchase transplants only from reputable sources',
      'Control aphids from early in the season - even a single brief feeding event can transmit the virus permanently',
      'Grow resistant varieties of cucumber and tomato where mosaic virus is a documented problem in your area',
      'A garden with strong biological activity and diverse beneficial insects will have lower aphid populations and therefore lower virus transmission risk. Regular compost tea applications and a diverse flowering understory are your best preventive investment',
    ],
  },

  {
    id: 'septoria-leaf-spot',
    image: '/images/pests/septoria-leaf-spot.png',
    name: 'Septoria Leaf Spot',
    type: 'disease',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['rain', 'humid'],
    description: 'A fungal disease caused by Septoria lycopersici that is one of the most common foliar diseases of tomatoes worldwide. While it rarely kills plants outright, it causes progressive defoliation that reduces photosynthesis, weakens plants, and reduces yield. The spores live in infected debris in the soil and are splash-distributed to lower leaves.',
    symptoms: [
      'Small, circular spots with white or grey centres and distinctive dark brown borders',
      'Tiny black dots visible within spot centres - these are the fungal fruiting bodies (pycnidia)',
      'Spots appearing first on the lowest, oldest leaves and advancing progressively upward',
      'Heavy spotting causing leaves to yellow fully and drop prematurely',
      'Leaves appearing tattered with masses of spots coalescing',
    ],
    affectedPlants: ['Tomato', 'Eggplant', 'Bell Pepper', 'Jalapeño Pepper', 'Celery'],
    organicSolutions: [
      { name: 'Remove Affected Leaves', description: 'Remove spotted leaves as soon as they are noticed, working from the base upward. Place in household waste - never compost. Regular leaf removal dramatically slows the upward progression of the disease through the season.' },
      { name: 'Copper-Based Fungicide', description: 'Copper spray applied preventively at transplanting and every 7–10 days provides significant protection. After symptoms appear, copper slows spread but does not cure affected tissue. Apply immediately after removing infected leaves. Use sparingly — copper accumulates in soil over time and can harm earthworms and soil biology at high concentrations.' },
      { name: 'Thick Mulch', description: 'A deep straw or wood chip mulch (8 - 10 cm) around the base of all nightshade plants prevents rain from splashing soil particles - and the spores living in them - onto the lower leaves. This single measure substantially slows disease progression.' },
    ],
    preventionTips: [
      'Mulch generously around the base of all tomato family crops to prevent soil splash - this addresses the primary infection route',
      'Water only at the base of plants and never use overhead irrigation for tomatoes, especially in the evening',
      'Rotate tomato crops on a 3–4 year schedule and clear all crop debris from the bed at the end of each season',
      'Apply compost tea as a foliar spray every two weeks from transplant time — the beneficial microorganisms colonise leaf surfaces and create a biological barrier that competes with Septoria spore germination',
    ],
  },

  {
    id: 'aster-yellows',
    image: '/images/pests/aster-yellows.png',
    name: 'Aster Yellows',
    type: 'disease',
    tags: ['yellowing', 'stunted', 'wilt'],
    weatherTriggers: ['warm'],
    description: 'A phytoplasma disease spread by leafhoppers that distorts and yellows flowers and foliage, often causing a bushy witches-broom appearance in affected plants.',
    symptoms: [
      'Yellowing and stunting of foliage, often with a yellowed rosette of distorted leaves at the crown',
      'Flowers turning green and leafy instead of forming normal petals',
      'Bunchy, tufted new growth and side shoots giving a witches-broom appearance',
    ],
    affectedPlants: ['Carrot', 'Lettuce', 'Celery', 'Onion', 'Strawberry', 'Zinnia', 'Aster'],
    organicSolutions: [
      {
        name: 'Remove infected plants',
        description: 'Pull out and destroy all clearly infected plants immediately - there is no cure and they will continue to spread the disease via leafhopper vectors.',
      },
      {
        name: 'Control leafhoppers',
        description: 'Reduce the leafhopper population with reflective mulch, row covers, and insecticidal soap sprays to limit the vector that spreads the disease.',
      },
    ],
    preventionTips: [
      'Use row covers from planting to exclude leafhopper vectors throughout the season',
      'Remove weeds around the garden perimeter which harbour both the phytoplasma and its leafhopper vectors',
      'Avoid planting susceptible crops adjacent to established perennial plantings where leafhoppers overwinter',
    ],
  },

  {
    id: 'bacterial-canker',
    image: '/images/pests/bacterial-canker.png',
    name: 'Bacterial Canker',
    type: 'disease',
    tags: ['wilt', 'spots', 'yellowing'],
    weatherTriggers: ['wet-soil', 'rain', 'cool'],
    description: 'A bacterial disease of stone fruits causing sunken oozing cankers on branches, shot-hole in leaves, and blossom dieback, often girdling branches completely.',
    symptoms: [
      'Sunken, dead patches on bark weeping amber gum in spring',
      'Round brown spots on leaves that fall out leaving a ragged shot-hole appearance',
      'Dead buds and blossoms failing to open in spring; branch dieback beyond a canker',
    ],
    affectedPlants: ['Cherry', 'Plum', 'Peach', 'Nectarine', 'Apple', 'Pear'],
    organicSolutions: [
      {
        name: 'Pruning infected wood',
        description: 'Cut out all cankered branches to at least 10 cm below visible infection during dry weather in summer; sterilise tools between cuts with methylated spirits.',
      },
      {
        name: 'Copper spray',
        description: 'Apply a copper-based spray to all leaf and shoot surfaces at leaf fall and again at bud swell to reduce bacterial inoculum on wound surfaces.',
      },
    ],
    preventionTips: [
      'Prune stone fruits only during dry summer weather when bacterial infection through wounds is least likely',
      'Avoid water stress and waterlogging, both of which weaken tree defences against bacterial entry',
      'Choose resistant or tolerant varieties where possible, particularly in wet climates',
    ],
  },

  {
    id: 'bacterial-leaf-spot',
    image: '/images/pests/bacterial-leaf-spot.png',
    name: 'Bacterial Leaf Spot',
    type: 'disease',
    tags: ['spots', 'yellowing', 'wilt'],
    weatherTriggers: ['rain', 'humid', 'warm'],
    description: 'A bacterial disease causing angular water-soaked spots on leaves bounded by leaf veins, which turn brown and may fall out leaving ragged holes.',
    symptoms: [
      'Angular water-soaked spots on leaves, turning brown with yellow halos',
      'Spots falling out leaving tattered holes in the leaf',
      'Lesions on stems and fruit surfaces in wet conditions',
    ],
    affectedPlants: ['Pepper', 'Tomato', 'Cucumber', 'Lettuce', 'Brassica', 'Bean', 'Beetroot'],
    organicSolutions: [
      {
        name: 'Copper-based spray',
        description: 'Apply copper hydroxide or copper oxychloride spray as a protectant during wet weather, coating all leaf surfaces before symptoms appear.',
      },
      {
        name: 'Remove infected leaves',
        description: 'Pick off and destroy spotted leaves as soon as they appear to slow the spread of bacteria through splashing water.',
      },
    ],
    preventionTips: [
      'Avoid overhead irrigation and water early in the day so foliage dries quickly',
      'Use certified disease-free seed and resistant varieties where available',
      'Rotate susceptible crops with non-hosts for at least 2 years between plantings',
    ],
  },

  {
    id: 'clubroot',
    image: '/images/pests/clubroot.png',
    name: 'Clubroot',
    type: 'disease',
    tags: ['wilt', 'stunted', 'yellowing'],
    weatherTriggers: ['wet-soil', 'cool'],
    description: 'A soil-borne slime mould disease that infects brassica roots, causing large distorted swollen galls that prevent normal water and nutrient uptake.',
    symptoms: [
      'Plants wilting dramatically in warm weather even when soil is moist',
      'Yellowing leaves and stunted, unproductive plants',
      'Roots pulled up to reveal large distorted swollen clubs instead of normal root structures',
    ],
    affectedPlants: ['Brassica', 'Radish', 'Turnip', 'Swede'],
    organicSolutions: [
      {
        name: 'Lime the soil',
        description: 'Apply ground limestone to raise soil pH above 7.0 - the disease is severe in acid soils and significantly reduced at higher pH levels.',
      },
      {
        name: 'Improve drainage',
        description: 'Raise beds or add grit to improve drainage as the pathogen thrives in waterlogged, compacted soils.',
      },
    ],
    preventionTips: [
      'Never move soil or plant material from a known infested plot to a clean area - spores persist in soil for 20+ years',
      'Grow brassicas on a minimum 7-year rotation and be strict about not breaking it',
      'Start transplants in sterile compost-filled modules rather than direct sowing to give plants a clean start',
    ],
  },

  {
    id: 'woodiness-virus',
    image: '/images/pests/woodiness-virus.png',
    name: 'Passionfruit Woodiness Virus',
    type: 'disease',
    tags: ['stunted', 'yellowing', 'wilt'],
    weatherTriggers: ['warm'],
    description: 'A virus spread by aphids causing fruit pulp to become dry and woody with little juice, leaves to mottle and distort, and overall vine decline.',
    symptoms: [
      'Fruit skin puckering and remaining hard; interior becomes woody with little juice',
      'Leaves showing yellow mosaic mottling and distorted curling',
      'Reduced flowering and fruiting; vine losing vigour over multiple seasons',
    ],
    affectedPlants: ['Passionfruit'],
    organicSolutions: [
      {
        name: 'Remove infected plants',
        description: 'Dig out and destroy any vine showing virus symptoms promptly - there is no cure and aphids will spread it to neighbouring vines.',
      },
      {
        name: 'Aphid control',
        description: 'Control aphid populations with reflective mulch and insecticidal soap sprays to reduce spread of the virus to healthy plants.',
      },
    ],
    preventionTips: [
      'Source virus-free certified planting stock from reputable nurseries only',
      'Do not propagate from any vine that has shown symptoms',
      'Keep aphid populations low throughout the growing season as they are the primary vector',
    ],
  },

  {
    id: 'bacterial-spot',
    image: '/images/pests/bacterial-spot.png',
    name: 'Bacterial Spot',
    type: 'disease',
    tags: ['spots', 'fruit-damage', 'yellowing'],
    weatherTriggers: ['rain', 'humid', 'warm'],
    description: 'A bacterial disease of stone fruits causing dark water-soaked spots on leaves, fruit, and twigs, leading to defoliation and surface scarring of fruit.',
    symptoms: [
      'Small water-soaked spots on leaves turning dark brown with yellow halos',
      'Spots falling out of leaves leaving a ragged shot-hole pattern',
      'Sunken, dark, scabby spots on fruit surfaces reducing quality and market value',
    ],
    affectedPlants: ['Peach', 'Nectarine', 'Plum', 'Cherry', 'Tomato', 'Pepper'],
    organicSolutions: [
      {
        name: 'Copper spray',
        description: 'Apply copper-based bactericide at bud break and again at petal fall, repeating every 10 days during wet periods to protect new growth.',
      },
      {
        name: 'Remove infected material',
        description: 'Prune out infected twigs with a dark blighted appearance during dry weather; disinfect tools between cuts.',
      },
    ],
    preventionTips: [
      'Choose resistant or tolerant varieties - resistance ratings are listed in many stone fruit nursery catalogues',
      'Avoid overhead irrigation and prune to open canopy structure to reduce leaf-wetness duration',
      'Avoid high nitrogen applications that promote soft, susceptible shoot growth',
    ],
  },

  // ── FUNGAL ──────────────────────────────────────────────────────────────────

  {
    id: 'botrytis-gray-mold',
    image: '/images/pests/botrytis-gray-mold.png',
    name: 'Botrytis (Gray Mold)',
    type: 'fungal',
    tags: ['spots'],
    weatherTriggers: ['rain', 'humid', 'cool'],
    description: 'Botrytis cinerea is one of the most widespread fungal diseases affecting both garden plants and stored produce. It infects primarily through damaged, dying, or senescent tissue and spreads rapidly in cool, humid, still-air conditions. Soft fruits - strawberries, raspberries, grapes - are particularly vulnerable at harvest.',
    symptoms: [
      'Fuzzy grey-brown mould growing on leaves, stems, flowers, or fruit',
      'Infected tissue becoming soft, brown, and water-soaked beneath the mould',
      'Flowers and petals turning brown and developing grey fuzz rather than dropping cleanly',
      'Strawberries, raspberries, and grapes rotting with characteristic grey mould coating',
      'A puff of grey-brown spores released when infected material is disturbed',
    ],
    affectedPlants: ['Strawberry', 'Grape', 'Tomato', 'Lettuce', 'Zinnia', 'Dahlia', 'Peach', 'Raspberry', 'Blackberry', 'Bee Balm', 'Calendula', 'Borage'],
    organicSolutions: [
      { name: 'Remove Affected Material Immediately', description: 'At the first sign of grey mould, remove all infected tissue carefully and dispose of in household waste. Handle infected parts gently - disturbing grey mould releases massive clouds of spores. Act before the mould sporulates if possible.' },
      { name: 'Improve Air Circulation', description: 'Botrytis thrives in still, humid air. Thin dense foliage, remove overlapping leaves, and ensure adequate spacing between plants. A light, open canopy is the most reliable long-term preventive measure.' },
      { name: 'Potassium Bicarbonate Spray', description: 'Potassium bicarbonate spray raises leaf surface pH, inhibiting Botrytis spore germination. Apply every 7 - 10 days in humid weather as a preventive. More effective before infection establishes than after.' },
      { name: 'Avoid Overhead Watering', description: 'Water at the base of plants. Wet foliage and wet flowers are the primary driver of Botrytis spread. Allow the canopy to dry completely. Where possible, use drip irrigation.' },
    ],
    preventionTips: [
      'Remove spent flowers and ageing foliage promptly - Botrytis enters primarily through senescent or damaged tissue, not healthy living plant material',
      'Harvest strawberries and soft fruits at peak ripeness; do not leave overripe fruit on the plant as it is the primary source of inoculum',
      'Space plants generously and avoid dense planting schemes in humid gardens',
      'Apply compost tea as a foliar spray during high-risk periods — plants with biologically active leaf surfaces are measurably more resistant to Botrytis. The beneficial organisms in compost tea occupy the micro-sites where Botrytis spores would otherwise germinate',
    ],
  },

  {
    id: 'rust',
    image: '/images/pests/rust.png',
    name: 'Rust',
    type: 'fungal',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['humid', 'warm', 'rain'],
    description: 'A large group of highly host-specific fungal diseases producing characteristic rust-orange powdery pustules on leaf surfaces. Each rust species infects a particular host plant. Leek rust, allium rust, and mint rust are among the most common in vegetable and herb gardens. Rust weakens plants and causes premature leaf drop in severe cases.',
    symptoms: [
      'Orange, yellow, or rust-brown powdery pustules on the underside of leaves',
      'Corresponding pale yellow or chlorotic spots on the upper leaf surface above the pustules',
      'Leaves yellowing, browning, and dropping prematurely under heavy infection',
      'Fine orange-brown powder released when pustules are touched',
      'Overall weakening of the plant and reduced vigour in severe infections',
    ],
    affectedPlants: ['Garlic', 'Onion', 'Leek', 'Chives', 'Asparagus', 'Mint', 'Raspberry', 'Blackberry', 'Apple', 'Pear', 'Bee Balm', 'Calendula', 'Yarrow'],
    organicSolutions: [
      { name: 'Remove Affected Leaves', description: 'Remove and dispose of all leaves showing rust pustules in household waste. This removes sporulating tissue and slows spread to adjacent leaves and plants. Do not handle other plants without washing hands first.' },
      { name: 'Elemental Sulphur Spray', description: 'Wettable elemental sulphur is an organically approved treatment effective against rust fungi. Apply at the first sign of infection and repeat every 7–14 days. Do not apply when temperatures exceed 30°C as phytotoxicity (leaf burn) may result. Do not apply within 2 weeks of an oil spray.' },
      { name: 'Neem Oil', description: 'Neem oil applied preventively, or in the very early stages of infection, disrupts the fungal life cycle and slows pustule formation. Apply every 7 - 10 days through the growing season on susceptible crops.' },
    ],
    preventionTips: [
      'Rotate alliums (garlic, onion, leek) on a 3–4 year cycle - rust spores overwinter in infected leaf debris and in the soil',
      'Harvest allium leaves regularly to reduce canopy density and age, which creates the humid microclimate rust needs',
      'Avoid wetting foliage when watering alliums, mint, and other rust-susceptible plants',
      'Spray aerated compost tea as a foliar application in spring before symptoms appear — the beneficial microorganisms compete with rust spores on leaf surfaces and significantly reduce infection rates on alliums grown in healthy, compost-rich soil',
    ],
  },

  {
    id: 'anthracnose',
    image: '/images/pests/anthracnose.png',
    name: 'Anthracnose',
    type: 'fungal',
    tags: ['spots'],
    weatherTriggers: ['rain', 'humid', 'warm'],
    description: 'A group of related fungal diseases (primarily Colletotrichum species) causing dark, sunken lesions on fruits, leaves, and stems. Each strain is host-specific. It is most destructive on ripening fruits and berries, with infected produce rotting rapidly. Spores spread via water splash and are most active in warm, humid conditions.',
    symptoms: [
      'Dark, circular, sunken spots appearing on ripe or ripening fruit',
      'Spots enlarge and may develop concentric rings; salmon-pink spore masses form in the centre of older lesions',
      'Dark, water-soaked lesions on leaves, particularly near the margins',
      'Infected fruits rotting rapidly and becoming completely unusable',
      'Dark cankers forming on stems, particularly near the soil line',
    ],
    affectedPlants: ['Tomato', 'Cucumber', 'Watermelon', 'Cantaloupe', 'Strawberry', 'Raspberry', 'Blackberry', 'Apple', 'Peach', 'Cherry', 'Grape', 'Green Beans'],
    organicSolutions: [
      { name: 'Remove Infected Material', description: 'Remove and destroy all affected fruits, leaves, and stems immediately. The pathogen spreads rapidly through water droplets - removing sporulating tissue substantially reduces disease pressure.' },
      { name: 'Copper-Based Fungicide Spray', description: 'Copper fungicide applied preventively provides strong protection against anthracnose. Apply before conditions become favourable (warm, humid weather) and repeat after removing infected tissue to protect remaining healthy growth. Use sparingly — copper accumulates in soil over time and can harm earthworms and soil biology at high concentrations.' },
      { name: 'Drip or Soaker Irrigation', description: 'Replace overhead watering with drip or soaker irrigation. Anthracnose spores spread via water droplets splashing between plants - eliminating overhead leaf-wetting reduces infection rates substantially.' },
    ],
    preventionTips: [
      'Use drip irrigation or soaker hoses rather than overhead sprinklers for all susceptible crops',
      'Practice crop rotation - Colletotrichum overwinters in infected debris in the soil for 2 - 3 years',
      'Harvest fruit promptly at peak ripeness; overripe fruit on the vine provides primary entry points for infection',
    ],
  },

  {
    id: 'black-spot',
    image: '/images/pests/black-spot.png',
    name: 'Black Spot',
    type: 'fungal',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['rain', 'warm', 'humid'],
    description: 'A fungal disease primarily associated with roses but also affecting cane fruits in the garden (as related Diplocarpon species). It thrives in warm, wet conditions and can cause severe defoliation. The fungus overwinters on fallen leaves and infected canes, and spreads by rain splash in spring.',
    symptoms: [
      'Circular black spots with fringed or feathery edges on leaf surfaces - the irregular edge distinguishes it from other leaf spots',
      'Yellow halos of chlorotic tissue surrounding the black spots',
      'Leaves yellowing fully around the spots and dropping prematurely, often when plants look otherwise healthy',
      'Severe defoliation during warm, wet seasons',
      'Dark spots or lesions on young green canes in severe cases',
    ],
    affectedPlants: ['Raspberry', 'Blackberry'],
    organicSolutions: [
      { name: 'Remove Fallen Leaves', description: 'Collect and dispose of fallen infected leaves from beneath canes and brambles throughout the growing season and entirely at the end of the season. Overwintering spores on fallen leaves are the primary source of spring infection.' },
      { name: 'Baking Soda or Potassium Bicarbonate', description: 'Spray a solution of 1 tablespoon of potassium bicarbonate per litre of water onto foliage in early spring before symptoms appear. The alkaline surface pH inhibits spore germination. Reapply every 7 - 14 days in wet conditions.' },
      { name: 'Neem Oil', description: 'Regular neem oil applications throughout the growing season suppress black spot by disrupting the fungal life cycle. Most effective when begun as a preventive before infection establishes.' },
      { name: 'Copper Spray', description: 'Copper fungicide applied in early spring as buds begin to open provides strong preventive protection. Follow up every 2–3 weeks through the main growing season. Use sparingly — copper accumulates in soil over time and can harm earthworms and soil biology at high concentrations.' },
    ],
    preventionTips: [
      'Choose black spot resistant cane fruit varieties where available',
      'Prune out and dispose of infected canes entirely at the end of the season - they carry overwintering inoculum',
      'Mulch around the base of canes to prevent spore splash from fallen leaves back onto new growth in spring',
      'Apply compost tea as a foliar spray in early spring before symptoms appear — the beneficial microorganisms naturally suppress black spot spore germination and are most effective as a preventive measure at the start of the season',
    ],
  },

  {
    id: 'verticillium-wilt',
    image: '/images/pests/verticillium-wilt.png',
    name: 'Verticillium Wilt',
    type: 'fungal',
    tags: ['wilt', 'yellowing'],
    description: 'A soil-borne fungal disease caused by Verticillium dahliae and V. albo-atrum that colonises the vascular system similarly to Fusarium wilt. It affects a very wide range of plants and can persist in the soil for many decades. It is most severe in cool soils in spring and autumn, and particularly problematic on strawberries and stone fruits.',
    symptoms: [
      'Sudden wilting of individual branches or one side of the plant on warm days, recovering at night initially - the partial recovery distinguishes it from late blight',
      'V-shaped yellow lesion developing from the edge of affected leaves inward',
      'Brown or dark discolouration in vascular tissue when the stem is cut - especially visible in a ring or V pattern near the base',
      'Stunted growth and gradual plant decline over weeks',
      'Older leaves yellowing and dropping first, then whole plant collapse',
    ],
    affectedPlants: ['Tomato', 'Eggplant', 'Bell Pepper', 'Jalapeño Pepper', 'Strawberry', 'Raspberry', 'Blackberry', 'Peach', 'Cherry', 'Mint'],
    organicSolutions: [
      { name: 'Soil Solarisation', description: 'Cover the affected bed with clear plastic sheeting for 4 - 6 weeks in summer. Solar heating reduces Verticillium populations in the top 15 - 20 cm substantially. Most effective in hot, sunny climates where soil temperatures reach 60°C.' },
      { name: 'Resistant Varieties', description: 'Many tomato and strawberry varieties carry the "V" resistance gene. When planting in known Verticillium-affected soil, always choose V-rated varieties as the primary management strategy.' },
      { name: 'Brassica Green Manure', description: 'Digging in brassica green manures (mustard, radish) releases glucosinolate compounds that act as soil biofumigants, suppressing soil-borne pathogens including Verticillium as they decompose. Grow, then cut and incorporate before plants set seed.' },
      { name: 'Build Soil Biology', description: 'Large additions of compost and organic matter build microbial diversity and populations of organisms naturally antagonistic to Verticillium. Consistent organic matter additions over several seasons significantly reduce pathogen populations.' },
    ],
    preventionTips: [
      'Implement strict crop rotation of at least 4 years for all affected plant families',
      'Never plant strawberries or other susceptible crops in beds where wilt-affected plants recently grew',
      'Build soil health consistently — high biological activity in soil naturally suppresses Verticillium through competitive exclusion. Consistent compost additions, compost tea applications, and diverse organic matter inputs are the most effective and durable strategy for managing Verticillium in the long term',
    ],
  },

  {
    id: 'apple-scab',
    image: '/images/pests/apple-scab.png',
    name: 'Apple Scab',
    type: 'fungal',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['rain', 'humid'],
    description: 'Venturia inaequalis is the most widespread apple disease in temperate climates. It overwinters in fallen leaves on the soil surface and releases spores in spring during wet weather, infecting young leaves and fruitlets. Infections in cool, wet springs can be severe enough to defoliate trees and render the entire crop unmarketable. Warm, dry summers significantly reduce infection pressure after initial spring infection.',
    symptoms: [
      'Olive-green to brown, velvety or powdery spots on the upper surface of young leaves in spring',
      'Yellow halos around spots on older leaves as infections expand; heavily infected leaves yellow and drop early',
      'Dark, sunken, or corky scabby patches on fruit skin, sometimes cracking as the fruit expands',
      'Fruit deformed or cracked where early infections occurred on the fruitlet',
      'Severe early defoliation leaving trees bare by midsummer in a bad infection year',
    ],
    affectedPlants: ['Apple', 'Crabapple', 'Pear'],
    organicSolutions: [
      { name: 'Lime Sulphur Spray', description: 'Apply lime sulphur at the green-tip to pink stage in spring, before first infections establish. This is the most effective organic fungicide timing for scab. Follow with sulphur-based sprays every 7 - 14 days during any wet period through petal fall. Lime sulphur can cause phytotoxicity in warm temperatures - do not apply above 24 degrees.' },
      { name: 'Sulphur-Based Fungicide', description: 'Wettable sulphur or sulphur dust applied every 7 - 10 days from green tip through the end of the primary infection period (6 weeks after petal fall) provides good protective coverage. Apply before rain events if possible, as spore release and infection require wet conditions. Sulphur is approved for organic use.' },
      { name: 'Rake and Remove Fallen Leaves', description: 'The fungus overwinters entirely in fallen leaves. Raking up and removing or composting leaves in a hot pile in autumn removes the majority of the overwintering inoculum and dramatically reduces the following spring\'s infection pressure. This is the single most impactful cultural practice available.' },
      { name: 'Copper Hydroxide Spray', description: 'Copper-based sprays applied at bud swell and again at tight cluster provide early-season protective coverage. Copper is toxic to the scab fungus at the spore germination stage. Use at the minimum effective rate to avoid copper accumulation in soil over many seasons.' },
    ],
    preventionTips: [
      'Rake up and remove or hot-compost all fallen leaves in autumn - this single step reduces the following season\'s primary inoculum by 70 - 90%',
      'Choose scab-resistant varieties where possible - Redfree, Liberty, Freedom, Pristine, and Enterprise have strong genetic resistance and require little or no spray programmes in most locations',
      'Prune to open canopy structure to improve airflow and accelerate leaf drying after rain - a well-pruned open-centred tree is significantly less susceptible than a dense unpruned one',
      'Apply the first protective spray at green tip before any symptoms appear - early protective sprays are far more effective than curative sprays after infection has occurred',
    ],
  },

  {
    id: 'alternaria-blight',
    image: '/images/pests/alternaria-blight.png',
    name: 'Alternaria Blight',
    type: 'fungal',
    tags: ['spots', 'yellowing', 'wilt'],
    weatherTriggers: ['humid', 'warm', 'rain'],
    description: 'A fungal disease causing dark bullseye-patterned spots on leaves and stems, often triggering early defoliation and reducing yield in warm, wet conditions.',
    symptoms: [
      'Dark brown circular spots with concentric ring patterns on older leaves',
      'Yellow halos surrounding spots, causing premature leaf yellowing and drop',
      'Dark sunken lesions on fruit and stem collars in severe cases',
    ],
    affectedPlants: ['Tomato', 'Potato', 'Carrot', 'Brassica', 'Pepper', 'Beetroot', 'Onion'],
    organicSolutions: [
      {
        name: 'Copper spray',
        description: 'Apply copper-based fungicide spray preventatively from transplanting, repeating every 7 - 14 days during warm wet weather.',
      },
      {
        name: 'Remove infected leaves',
        description: 'Strip and destroy spotted leaves as soon as they appear to reduce spore load and slow the spread up the plant.',
      },
    ],
    preventionTips: [
      'Rotate crops for at least 3 years to prevent spore build-up in the soil',
      'Avoid overhead irrigation and water at the base of plants in the morning',
      'Choose resistant varieties where catalogue listings indicate Alternaria resistance',
    ],
  },

  {
    id: 'brown-rot',
    image: '/images/pests/brown-rot.png',
    name: 'Brown Rot',
    type: 'fungal',
    tags: ['rot', 'mold', 'fruit-damage'],
    weatherTriggers: ['humid', 'warm', 'rain'],
    description: 'A fungal disease that causes rapid rotting of fruit on the tree, producing distinctive concentric rings of powdery buff-coloured spore pustules.',
    symptoms: [
      'Soft brown rotting patches on ripe or near-ripe fruit, spreading rapidly',
      'Concentric rings of buff or cream spore pustules on the rotted surface',
      'Mummified fruits hanging on the tree through winter',
    ],
    affectedPlants: ['Apple', 'Pear', 'Plum', 'Cherry', 'Peach', 'Nectarine', 'Quince'],
    organicSolutions: [
      {
        name: 'Remove infected fruit',
        description: 'Pick off and destroy all rotting fruit on the tree and from the ground immediately - never leave mummies on branches over winter.',
      },
      {
        name: 'Improve air circulation',
        description: 'Prune trees to an open structure to reduce humidity in the canopy and speed leaf drying after rain.',
      },
    ],
    preventionTips: [
      'Thin fruits in June to prevent touching pairs that trap moisture and allow fungal entry',
      'Control codling moth and other fruit-piercing pests as wounds are the primary entry point',
      'Remove and hot-compost all mummified fruits and fallen leaves during winter',
    ],
  },

  {
    id: 'cane-blight',
    image: '/images/pests/cane-blight.png',
    name: 'Cane Blight',
    type: 'fungal',
    tags: ['wilt', 'spots', 'yellowing'],
    weatherTriggers: ['humid', 'rain'],
    description: 'A fungal disease of raspberry and blackberry canes causing dark lesions at the base of laterals, wilting of fruiting shoots, and cane death.',
    symptoms: [
      'Dark purple or brown lesions at the base of fruiting laterals in summer',
      'Sudden wilting and death of fruiting laterals with leaves drying on the cane',
      'Bark splitting and darkening on infected canes when pressed',
    ],
    affectedPlants: ['Raspberry', 'Blackberry', 'Loganberry'],
    organicSolutions: [
      {
        name: 'Remove infected canes',
        description: 'Cut out and burn all infected canes immediately at the base, disinfecting secateurs between cuts.',
      },
      {
        name: 'Copper spray',
        description: 'Apply copper fungicide to canes at bud burst and again after harvest to protect fresh wound sites from spore entry.',
      },
    ],
    preventionTips: [
      'Avoid injury to canes when tying in or training as wounds are the primary infection point',
      'Space canes widely and remove old canes promptly after fruiting to improve airflow',
    ],
  },

  {
    id: 'cane-spot',
    image: '/images/pests/cane-spot.png',
    name: 'Cane Spot',
    type: 'fungal',
    tags: ['spots', 'wilt'],
    weatherTriggers: ['humid', 'rain'],
    description: 'A fungal disease causing small purple-centred elliptical spots on raspberry and loganberry canes that enlarge into pale-centred cankers, sometimes girdling the cane.',
    symptoms: [
      'Small purple spots on young canes in spring, enlarging with pale centres',
      'Spots joining to form cankers that crack and girdle the cane',
      'Fruiting laterals withering above a girdling canker',
    ],
    affectedPlants: ['Raspberry', 'Loganberry'],
    organicSolutions: [
      {
        name: 'Pruning',
        description: 'Cut out and destroy all spotted canes immediately after fruiting; do not leave old canes standing into the following season.',
      },
      {
        name: 'Copper spray',
        description: 'Apply copper fungicide at bud burst as a protective treatment, particularly in wet springs.',
      },
    ],
    preventionTips: [
      'Maintain good spacing between canes to improve airflow and reduce leaf-to-cane wetness duration',
      'Avoid overhead irrigation which wets canes and promotes spore germination',
    ],
  },

  {
    id: 'cercospora-leaf-spot',
    image: '/images/pests/cercospora-leaf-spot.png',
    name: 'Cercospora Leaf Spot',
    type: 'fungal',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['humid', 'warm', 'rain'],
    description: 'A fungal disease causing circular grey or tan spots with dark borders on beetroot, chard, and spinach leaves, which enlarge and merge to cause early defoliation.',
    symptoms: [
      'Circular spots with pale grey centres and dark purple-brown margins on leaves',
      'Spots enlarging and merging, covering large leaf areas in warm humid weather',
      'Premature yellowing and loss of older leaves from the outside of the plant inward',
    ],
    affectedPlants: ['Beetroot', 'Chard', 'Spinach', 'Bean', 'Celery'],
    organicSolutions: [
      {
        name: 'Remove infected leaves',
        description: 'Remove spotted leaves promptly and destroy them to reduce the spore reservoir and delay spread to younger growth.',
      },
      {
        name: 'Copper spray',
        description: 'Apply copper-based spray preventatively during warm humid conditions, coating both upper and lower leaf surfaces thoroughly.',
      },
    ],
    preventionTips: [
      'Practice a 3-year rotation for all chenopod family crops',
      'Avoid overhead watering and water only at the base of plants in the morning',
    ],
  },

  {
    id: 'fusarium-crown-rot',
    image: '/images/pests/fusarium-crown-rot.png',
    name: 'Fusarium Crown and Root Rot',
    type: 'fungal',
    tags: ['wilt', 'rot', 'yellowing', 'stunted'],
    weatherTriggers: ['warm', 'wet-soil'],
    description: 'A soil-borne fungal disease that rots the crown and roots of asparagus and other crops, causing progressive dieback of ferns and eventual loss of the planting.',
    symptoms: [
      'Ferns or shoots wilting and dying from the tips downward',
      'Crown and upper roots turning reddish-brown and rotting when dug up',
      'Thin, pale, weakened spears emerging from an infected crown',
    ],
    affectedPlants: ['Asparagus', 'Strawberry', 'Bean', 'Pepper', 'Tomato'],
    organicSolutions: [
      {
        name: 'Improve drainage',
        description: 'Install drainage channels or raise beds to prevent waterlogging around crowns - saturated soil is the primary predisposing factor.',
      },
      {
        name: 'Remove infected plants',
        description: 'Dig out and destroy infected crowns including all roots; do not replant with susceptible crops on that spot for several years.',
      },
    ],
    preventionTips: [
      'Plant asparagus crowns on raised beds with excellent drainage',
      'Avoid injury to crowns during cultivation as wounds are entry points for the fungus',
      'Start with disease-free certified crowns from reputable suppliers',
    ],
  },

  {
    id: 'leek-rust',
    image: '/images/pests/leek-rust.png',
    name: 'Leek Rust',
    type: 'fungal',
    tags: ['spots', 'yellowing'],
    weatherTriggers: ['humid', 'cool', 'rain'],
    description: 'A rust fungus causing bright orange powdery pustules on leek and allium leaves, weakening plants and reducing bulb and stem size but rarely killing established crops.',
    symptoms: [
      'Bright orange oval pustules scattered across leaf surfaces',
      'Pustules rupturing to release orange powdery spores that rub off on contact',
      'Leaves yellowing and dying back from tips downward in severe infections',
    ],
    affectedPlants: ['Leek', 'Onion', 'Garlic', 'Chive', 'Allium'],
    organicSolutions: [
      {
        name: 'Remove infected leaves',
        description: 'Cut off and destroy the most heavily infected leaves to reduce the spore load and improve plant ventilation.',
      },
      {
        name: 'Sulphur spray',
        description: 'Apply sulphur-based fungicide spray to reduce spread during cool wet autumn weather when infections are worst.',
      },
    ],
    preventionTips: [
      'Space leeks widely to encourage good air movement between plants',
      'Avoid high nitrogen feeding in late summer which produces soft, susceptible growth',
      'Rotate allium crops to avoid infected soil debris',
    ],
  },

  {
    id: 'mint-rust',
    image: '/images/pests/mint-rust.png',
    name: 'Mint Rust',
    type: 'fungal',
    tags: ['spots', 'yellowing', 'stunted'],
    weatherTriggers: ['humid', 'cool'],
    description: 'A rust fungus causing distorted and swollen shoot tips in spring and bright orange spore pustules on leaves and stems through summer and autumn.',
    symptoms: [
      'Pale, swollen, distorted shoot tips and stems in spring with no obvious pustules initially',
      'Bright orange pustules on undersides and upper surfaces of leaves from early summer',
      'Premature leaf yellowing and drop in heavily infected plants',
    ],
    affectedPlants: ['Mint'],
    organicSolutions: [
      {
        name: 'Remove infected shoots',
        description: 'Cut back all infected stems to ground level and destroy them - the fungus overwinters systemically inside the rhizomes.',
      },
      {
        name: 'Heat treatment of runners',
        description: 'Submerge dormant mint runners in water heated to 44 C for 10 minutes before planting to kill systemic fungal infections in the rhizome.',
      },
    ],
    preventionTips: [
      'Start new mint plantings from heat-treated runners in fresh uncontaminated soil',
      'Avoid overhead watering which keeps foliage wet and encourages spore germination',
      'Allow good air movement through the planting by dividing and thinning every 2 - 3 years',
    ],
  },

  {
    id: 'mummyberry',
    image: '/images/pests/mummyberry.png',
    name: 'Mummyberry',
    type: 'fungal',
    tags: ['wilt', 'rot', 'fruit-damage'],
    weatherTriggers: ['cool', 'rain', 'humid'],
    description: 'A two-stage fungal disease of blueberries causing shoot blight in spring followed by infected berries that shrivel into hard tan mummified fruits.',
    symptoms: [
      'Shoot tips wilting and turning brown as if frosted, with salmon-coloured spores inside',
      'Berries turning tan, shrivelling, and becoming hard before they ripen',
      'Mummified fruits dropping or remaining attached through winter',
    ],
    affectedPlants: ['Blueberry'],
    organicSolutions: [
      {
        name: 'Mulch deeply',
        description: 'Apply 10 cm of wood chip or pine bark mulch under bushes in early spring to bury mummified fruit apothecia and prevent spore release.',
      },
      {
        name: 'Remove mummies',
        description: 'Rake and destroy all mummified fruits from the ground and canopy before bud break to remove the primary inoculum source.',
      },
    ],
    preventionTips: [
      'Apply fresh mulch every spring as the key preventive measure - deeply buried mummies cannot release spores',
      'Rake and remove all dropped fruit through the growing season',
    ],
  },

  {
    id: 'neck-rot',
    image: '/images/pests/neck-rot.png',
    name: 'Neck Rot',
    type: 'fungal',
    tags: ['rot', 'mold', 'wilt'],
    weatherTriggers: ['humid', 'wet-soil', 'cool'],
    description: 'A fungal disease of stored onions and garlic that causes softening and rotting around the neck, often not becoming visible until weeks after harvest.',
    symptoms: [
      'Soft, sunken, water-soaked tissue just below the neck of stored bulbs',
      'Grey fluffy fungal growth visible at the neck when rotting advances',
      'Bulbs collapsing in storage, often with little external warning',
    ],
    affectedPlants: ['Onion', 'Garlic', 'Shallot'],
    organicSolutions: [
      {
        name: 'Cure thoroughly before storage',
        description: 'Dry harvested bulbs in warm sunny conditions for 2 - 3 weeks until necks are completely dry and papery before placing in storage.',
      },
      {
        name: 'Remove infected bulbs',
        description: 'Check stored bulbs every 1 - 2 weeks and remove any showing soft patches immediately before the rot spreads to neighbouring bulbs.',
      },
    ],
    preventionTips: [
      'Use certified disease-free seed or sets - the fungus is commonly seed-borne',
      'Avoid applying high nitrogen fertiliser late in the season which keeps necks soft and susceptible',
      'Store only fully cured, undamaged bulbs in cool, dry, well-ventilated conditions',
    ],
  },

  {
    id: 'onion-smut',
    image: '/images/pests/onion-smut.png',
    name: 'Onion Smut',
    type: 'fungal',
    tags: ['stunted', 'spots', 'wilt'],
    weatherTriggers: ['cool', 'wet-soil'],
    description: 'A soil-borne fungal disease that infects onion and leek seedlings in cool wet conditions, causing dark streaks on cotyledons and leaves and plant death.',
    symptoms: [
      'Dark olive-green to black streaks inside young leaves visible when split open',
      'Thickened, distorted leaves with dark powdery smut pustules',
      'Seedlings dying before or shortly after emergence in infested soil',
    ],
    affectedPlants: ['Onion', 'Leek', 'Shallot', 'Garlic'],
    organicSolutions: [
      {
        name: 'Grow indoors and transplant',
        description: 'Start seeds in clean sterile compost indoors - seedlings more than 3 cm tall at transplanting have largely passed the susceptible growth window.',
      },
      {
        name: 'Trichoderma biocontrol',
        description: 'Apply Trichoderma-based soil inoculant to the seed row at sowing to establish beneficial fungi that compete with the smut pathogen.',
      },
    ],
    preventionTips: [
      'Rotate alliums to a new bed every 3 - 4 years as spores persist in soil for many years',
      'Avoid sowing into cold, wet, compacted soil - wait until conditions improve or sow under cover',
    ],
  },

  {
    id: 'peach-leaf-curl',
    image: '/images/pests/peach-leaf-curl.png',
    name: 'Peach Leaf Curl',
    type: 'fungal',
    tags: ['yellowing', 'spots', 'wilt'],
    weatherTriggers: ['rain', 'cool'],
    description: 'A fungal disease that infects peach and nectarine leaves at bud break, causing dramatic red-blistered curling of leaves which drop early, weakening the tree.',
    symptoms: [
      'Leaves emerging distorted, thickened, and puckered with red or yellow blistering',
      'Infected leaves gradually turning grey-white as spores form on the surface',
      'Early leaf drop followed by a second flush of (usually healthy) growth',
    ],
    affectedPlants: ['Peach', 'Nectarine', 'Almond'],
    organicSolutions: [
      {
        name: 'Copper spray at bud swell',
        description: 'Apply copper fungicide spray at bud burst (when buds are just starting to swell but not yet open) - this is the single most effective intervention.',
      },
      {
        name: 'Rain shelter',
        description: 'Erect a temporary clear polythene shelter over fan-trained trees from January through to May to prevent the rain splash that carries spores to buds.',
      },
    ],
    preventionTips: [
      'Treat every year at bud swell even if the previous year was clean - spores survive on bark and buds',
      'Feed trees well with balanced nutrition to support a strong second growth flush after the infected leaves drop',
    ],
  },

  {
    id: 'pear-scab',
    image: '/images/pests/pear-scab.png',
    name: 'Pear Scab',
    type: 'fungal',
    tags: ['spots', 'fruit-damage', 'wilt'],
    weatherTriggers: ['rain', 'cool', 'humid'],
    description: 'A fungal disease of pears causing dark olive-green velvety spots on leaves and fruits, leading to cracked, deformed, and unmarketable fruit in wet seasons.',
    symptoms: [
      'Olive-green velvety spots on young leaves and shoots, yellowing beneath',
      'Dark green to black corky scabby patches on developing fruit',
      'Fruit cracking and deforming around scab lesions as they enlarge',
    ],
    affectedPlants: ['Pear'],
    organicSolutions: [
      {
        name: 'Copper or sulphur spray',
        description: 'Apply protective copper or sulphur spray from green tip through petal fall, repeating every 10 - 14 days and after any heavy rain.',
      },
      {
        name: 'Remove infected leaves',
        description: 'Rake and compost all fallen leaves in autumn to reduce the primary inoculum that overwinters on leaf litter.',
      },
    ],
    preventionTips: [
      'Choose scab-resistant pear varieties where possible',
      'Prune to an open structure to improve canopy airflow and accelerate leaf drying after rain',
      'Apply the first spray at green tip before symptoms appear - early protection is far more effective than curative treatment',
    ],
  },

  {
    id: 'white-rot',
    image: '/images/pests/white-rot.png',
    name: 'White Rot',
    type: 'fungal',
    tags: ['wilt', 'rot', 'yellowing'],
    weatherTriggers: ['cool', 'wet-soil'],
    description: 'A soil-borne fungal disease of alliums causing fluffy white fungal growth and black sclerotia at the base of bulbs, rotting roots and killing plants.',
    symptoms: [
      'Plants yellowing from older leaves inward; pulling up easily with few roots',
      'Dense white fluffy fungal growth at the bulb base with small black sclerotia embedded in it',
      'Entire bulb rotting from the base upward in severe cases',
    ],
    affectedPlants: ['Onion', 'Garlic', 'Shallot', 'Leek', 'Chive'],
    organicSolutions: [
      {
        name: 'Trichoderma biocontrol',
        description: 'Apply Trichoderma viride or T. harzianum-based product to the seed trench at planting to establish competing beneficial fungi.',
      },
      {
        name: 'Remove infected plants and soil',
        description: 'Dig out affected plants including a generous root ball of surrounding soil and dispose of in household waste, not compost.',
      },
    ],
    preventionTips: [
      'Avoid growing any allium on infected ground for at least 15 years - sclerotia survive in soil for decades',
      'Never share soil, tools, or allium material from an infected plot',
      'Lure sclerotia to germinate and die without a host by watering infested soil with a dilute garlic extract 6 weeks before planting',
    ],
  },

  {
    id: 'leaf-blight',
    image: '/images/pests/leaf-blight.png',
    name: 'Leaf Blight',
    type: 'fungal',
    tags: ['spots', 'yellowing', 'wilt'],
    weatherTriggers: ['humid', 'warm', 'rain'],
    description: 'A range of fungal pathogens causing rapid browning and death of large areas of leaf tissue, often spreading through a canopy quickly in warm, humid conditions.',
    symptoms: [
      'Large irregular brown or tan dead patches spreading rapidly across leaves',
      'Infected areas drying out and papery, often with a darker water-soaked margin',
      'Premature defoliation starting from the lowest, oldest leaves upward',
    ],
    affectedPlants: ['Tomato', 'Potato', 'Bean', 'Pea', 'Corn', 'Strawberry', 'Raspberry'],
    organicSolutions: [
      {
        name: 'Copper spray',
        description: 'Apply copper-based fungicide spray preventatively from the start of wet humid weather, coating all leaf surfaces on both sides.',
      },
      {
        name: 'Remove infected leaves',
        description: 'Strip and destroy all blighted leaves as soon as they appear to slow the spread upward through the canopy.',
      },
    ],
    preventionTips: [
      'Water only at the base of plants and never use overhead irrigation during humid weather',
      'Rotate crops with non-host families for a minimum of 3 years',
      'Choose varieties with documented blight tolerance where available',
    ],
  },

  {
    id: 'potato-scab',
    image: '/images/pests/potato-scab.png',
    name: 'Potato Scab',
    type: 'fungal',
    tags: ['spots', 'fruit-damage'],
    weatherTriggers: ['dry', 'warm'],
    description: 'A soil-borne pathogen causing rough, corky, raised or pitted scabs on the skin of potato and beetroot tubers; plants remain healthy but tubers are blemished.',
    symptoms: [
      'Rough, raised or pitted corky patches on potato skin at harvest',
      'Scabs ranging from superficial russet to deep pitted lesions in severe cases',
      'No visible above-ground symptoms - plants grow and crop normally',
    ],
    affectedPlants: ['Potato', 'Beetroot', 'Parsnip', 'Radish', 'Swede'],
    organicSolutions: [
      {
        name: 'Water consistently during tuber set',
        description: 'Keep soil evenly moist for the 6 weeks after flowering begins - this is when tubers are most susceptible and dry soil greatly increases scab severity.',
      },
      {
        name: 'Acidify with pine needles or sulphur',
        description: 'Incorporate pine needle mulch or apply sulphur to lower soil pH below 5.5 - scab is significantly reduced in acid soils.',
      },
    ],
    preventionTips: [
      'Grow scab-resistant varieties such as Maris Piper, Cara, or Sarpo Mira',
      'Avoid liming potato beds or applying fresh manure in the year before planting',
      'Rotate potatoes to a fresh bed each year as the pathogen persists in soil',
    ],
  },

  {
    id: 'smut',
    image: '/images/pests/smut.png',
    name: 'Corn Smut',
    type: 'fungal',
    tags: ['stunted', 'wilt', 'spots'],
    weatherTriggers: ['warm', 'dry'],
    description: 'A fungal disease that replaces corn kernels with large silver-grey galls that burst to release masses of black powdery spores, dramatically reducing yield.',
    symptoms: [
      'Large, swollen, silver-grey galls on ears, tassels, or leaves, up to 15 cm across',
      'Galls turning dark and rupturing to release masses of black smut spores',
      'Affected plants looking otherwise healthy until galls form',
    ],
    affectedPlants: ['Corn'],
    organicSolutions: [
      {
        name: 'Remove galls before they burst',
        description: 'Cut out all silver-grey galls and seal them in a bag for disposal before they darken and rupture - this prevents spores entering the soil.',
      },
      {
        name: 'Resistant varieties',
        description: 'Choose corn varieties with good smut tolerance - sweet corn varieties vary considerably in their susceptibility.',
      },
    ],
    preventionTips: [
      'Avoid injuring plants during cultivation as wounds are a primary infection point for spores',
      'Do not compost infected material - smut spores survive hot composting',
      'Rotate corn to a fresh bed each year as spores persist in soil for several years',
    ],
  },

  // ── ANIMAL ─────────────────────────────────────────────────────────────────

  {
    id: 'deer',
    image: '/images/pests/deer.png',
    name: 'Deer',
    type: 'animal',
    tags: ['chewing', 'holes'],
    description: 'Deer can cause severe damage to gardens overnight, browsing a very wide range of plants. They are most active at dusk and dawn and are more destructive in late winter and early spring when natural forage is scarce. A single deer visit can destroy beds that took months to establish, and once deer find a food source they return reliably.',
    symptoms: [
      'Plants stripped of leaves and new growth overnight',
      'Ragged, torn leaf edges - deer have no upper front teeth and tear rather than cut cleanly',
      'Shrubs and young trees with bark rubbed or stripped at a consistent height',
      'Hoof prints (crescent-shaped slots) in soft soil near damaged plants',
      'Browsing damage concentrated at a consistent height of 1 - 1.5 m from the ground',
    ],
    affectedPlants: ['Lettuce', 'Kale', 'Spinach', 'Arugula', 'Broccoli', 'Swiss Chard', 'Beet', 'Carrot', 'Green Beans', 'Peas', 'Corn', 'Apple', 'Pear', 'Peach', 'Cherry', 'Raspberry', 'Blueberry', 'Strawberry', 'Sunflower', 'Dahlia', 'Cosmos'],
    organicSolutions: [
      { name: 'Deer Fencing', description: 'An 8-foot fence is the only fully reliable deer deterrent. Deer are excellent jumpers - a standard 6-foot fence will be cleared. An electric fence baited with peanut butter teaches deer to avoid the garden very effectively. A double fence of two lower fences set 1 m apart also works as deer are reluctant to jump into a narrow space.' },
      { name: 'Deer Repellent Sprays', description: 'Products based on putrefied egg solids or hot pepper exploit deer\'s acute sense of smell. Reapply after every rain and monthly through the growing season. Rotate between different products to prevent deer habituating to any single scent.' },
      { name: 'Motion-Activated Sprinklers', description: 'Motion-triggered sprinklers such as Scarecrow startle deer with a sudden burst of water. Very effective when relocated every few days as deer learn precise boundary positions. Works best along established deer travel paths.' },
      { name: 'Aromatic Barrier Plants', description: 'Plant strongly aromatic herbs along the garden perimeter - rosemary, lavender, sage, yarrow, and bee balm. Deer strongly dislike intensely scented plants and tend to avoid areas where these grow thickly.' },
    ],
    preventionTips: [
      'Install permanent fencing before deer become habituated to the garden - once they establish a feeding route they are very persistent',
      'Deer are most active at dusk and dawn - harvest vulnerable crops in the evening during periods of high pressure',
      'Grow particularly susceptible crops (lettuce, peas, beans) in enclosed raised beds with mesh covers where deer pressure is severe',
    ],
  },

  {
    id: 'rabbits',
    image: '/images/pests/rabbits.png',
    name: 'Rabbits',
    type: 'animal',
    tags: ['chewing', 'holes'],
    description: 'Rabbits can cause substantial damage to gardens, particularly in early spring when other forage is limited. They target tender young growth and low-growing crops and a small family group can cause losses disproportionate to their size. They make clean, angled cuts - the opposite of the ragged tearing caused by deer.',
    symptoms: [
      'Plants eaten cleanly down to stumps with precise 45-degree angled cuts on stems',
      'Low-growing crops disappearing overnight, particularly in early spring when other forage is scarce',
      'Seedlings removed entirely at soil level',
      'Small, round black droppings (5 - 8 mm) scattered near damage',
      'Bark gnawed cleanly from young fruit trees at ground level in winter, sometimes girdling stems',
    ],
    affectedPlants: ['Lettuce', 'Spinach', 'Kale', 'Arugula', 'Peas', 'Green Beans', 'Carrot', 'Beet', 'Radish', 'Turnip', 'Broccoli', 'Cabbage', 'Parsley', 'Basil', 'Apple', 'Pear', 'Raspberry', 'Strawberry'],
    organicSolutions: [
      { name: 'Wire Mesh Fencing', description: 'A 90 cm high fence of fine wire mesh (2.5 cm or smaller openings), buried 15 - 20 cm deep with the buried section bent outward (away from the garden), is the most reliable long-term solution. Rabbits are determined burrowers; the underground apron prevents tunnelling under.' },
      { name: 'Individual Plant Guards', description: 'Wrap small wire mesh cylinders (hardware cloth) around the base of young fruit trees and vulnerable transplants to protect stems from gnawing. Particularly important for bark protection in winter when other food is scarce.' },
      { name: 'Scent Deterrents', description: 'Blood meal, predator urine, human hair, and strongly-scented soap shavings can temporarily deter rabbits. These need regular renewal, particularly after rain, and become less effective as rabbits habituate over time. Use as a supplement to physical barriers.' },
      { name: 'Encourage Natural Predators', description: 'Foxes, hawks, and owls are effective rabbit predators. Avoid deterring these animals - an active hawk territory or resident fox significantly reduces rabbit populations. Install nest boxes for kestrels and barn owls.' },
    ],
    preventionTips: [
      'Install permanent perimeter fencing before rabbit populations build - exclusion is always easier and less effort than deterrence',
      'Protect all individual seedlings and transplants with wire cloches during the early growth period when they are most vulnerable',
      'Plant aromatic perimeter companions - lavender, sage, rosemary, and marigolds - along garden borders as a secondary deterrent',
    ],
  },

  {
    id: 'voles-moles',
    image: '/images/pests/voles-moles.png',
    name: 'Voles and Moles',
    type: 'animal',
    tags: ['wilt', 'stunted'],
    description: 'Voles are small rodents that create surface and subsurface tunnels and feed on roots, bulbs, and tubers. Moles are insectivores that eat grubs and earthworms - their tunnelling can uproot plants but they do not eat plant material. Both are present in most gardens; voles cause the primary plant damage.',
    symptoms: [
      'Surface runways - narrow (2 - 3 cm), pressed-flat tunnels in grass or through mulch - indicate voles',
      'Plants wilting or collapsing despite adequate watering - roots have been eaten below ground',
      'Root vegetables, bulbs, and tubers with gnawed cavities when harvested',
      'Large mounds of excavated soil pushed to the surface at intervals (moles)',
      'Plants pulled down into the soil or apparently sinking - signs of tunnelling beneath',
    ],
    affectedPlants: ['Carrot', 'Beet', 'Onion', 'Garlic', 'Strawberry', 'Sweet Potato', 'Peas', 'Corn', 'Asparagus', 'Leek', 'Turnip'],
    organicSolutions: [
      { name: 'Hardware Cloth Lining', description: 'Line raised beds and planting areas with 6 mm hardware cloth laid flat 15 - 30 cm below soil surface, with sides turned up to create a below-ground basket. This prevents voles accessing roots from below and is the most reliable long-term protection for root crops.' },
      { name: 'Castor Oil Drench', description: 'A solution of castor oil and dish soap applied as a soil drench is a well-documented mole and vole repellent. Castor oil is absorbed by the soil and creates a persistent odour that drives burrowing animals away. Apply every 2 - 3 weeks during active seasons.' },
      { name: 'Encourage Predators', description: 'Owls, kestrels, foxes, weasels, and cats are highly effective vole predators. Install owl and kestrel nest boxes, allow perching posts, and avoid all rodenticides - they kill the predators that would otherwise regulate vole populations.' },
      { name: 'Reduce Dense Ground Cover', description: 'Voles prefer moving under the cover of dense vegetation. Mow grass short around garden borders and maintain a cleared strip between long grass and planting beds, exposing voles to predators during movement.' },
    ],
    preventionTips: [
      'Line the base of all raised beds with hardware cloth - the most reliable and durable protection for root crops against vole damage',
      'Avoid deep mulch directly around the base of trees and shrubs in winter, where voles will nest and access bark for feeding',
      'Build habitat for predators proactively: owl boxes, perching posts, and undisturbed boundary areas with native vegetation',
    ],
  },

  {
    id: 'birds',
    image: '/images/pests/birds.png',
    name: 'Birds',
    type: 'animal',
    tags: ['holes', 'fruit-damage', 'chewing'],
    description: 'Various bird species that feed on fruit, seeds, seedlings, and soft growth; wood pigeons strip brassica leaves while smaller birds peck ripening fruit and freshly sown seed.',
    symptoms: [
      'Ripening fruit pecked open and hollowed out, particularly cherries, strawberries, and currants',
      'Brassica leaves stripped to the midrib by wood pigeons, especially in late autumn and winter',
      'Seed rows disappearing after sowing with no germination, or seedlings pulled out whole',
    ],
    affectedPlants: ['Cherry', 'Strawberry', 'Blueberry', 'Currant', 'Gooseberry', 'Brassica', 'Lettuce', 'Pea', 'Corn', 'Sunflower'],
    organicSolutions: [
      {
        name: 'Netting',
        description: 'Drape or erect fine bird-proof netting (less than 25 mm mesh) over fruit cages, brassica beds, and newly sown rows as the only reliably effective protection.',
      },
      {
        name: 'Visual deterrents',
        description: 'Hang old CDs, foil strips, or proprietary bird scarers near fruit as a temporary deterrent, moving them every few days as birds habituate quickly.',
      },
    ],
    preventionTips: [
      'Erect a permanent fruit cage with robust netting over soft fruit bushes and strawberry beds',
      'Cover brassica beds with netting from September through April when pigeon pressure is highest',
      'Sow seeds under cloches or fleece and leave in place until seedlings are established',
    ],
  },

];

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getPestById(id: string): Pest | undefined {
  return PESTS.find(p => p.id === id);
}

export function getPestsByType(type: Pest['type']): Pest[] {
  return PESTS.filter(p => p.type === type);
}

export function getPestsForPlant(plantName: string): Pest[] {
  const lower = plantName.toLowerCase();
  return PESTS.filter(pest =>
    pest.affectedPlants.some(name => name.toLowerCase() === lower)
  );
}
